// MarketingHub — /admin/marketing
// v2 (16-jun-2026): Dashboard compacto. Timeline 7 días + cola de aprobación inline.
// Header con countdown a D-Day (18-jun). KPIs en 4 columnas. Cards compactas
// para acciones rápidas. El mockup celular completo vive en /admin/marketing-agent.

import { useEffect, useState, useMemo } from 'react'
import { Link } from 'react-router-dom'
import AdminLayout from '../../components/AdminLayout'
import { supabase } from '../../lib/supabase'
import { CardLimpackFrame, isCardLimpack, LIMPACK_CARDS_CSS } from '../../components/marketing/cards/CardsLimpack'

const SUPA_URL = import.meta.env.VITE_SUPABASE_URL

// D-Day oficial Limpack
const D_DAY = new Date('2026-06-18T00:00:00-03:00')

const FORMATO_LABEL = {
  carrusel: 'Carrusel',
  post_simple: 'Post',
  feed: 'Feed',
  feed_vertical: 'Feed',
  story: 'Story',
  reel: 'Reel',
  short: 'Short',
  status: 'Status',
  video: 'Video',
  imagen: 'Imagen',
}

const FORMATO_ASPECT = {
  feed: '4:5',
  feed_vertical: '4:5',
  carrusel: '4:5',
  post_simple: '4:5',
  imagen: '4:5',
  story: '9:16',
  reel: '9:16',
  short: '9:16',
  status: '9:16',
  video: '9:16',
}

const PILAR_LABEL = {
  costo_invisible: 'Costo invisible',
  diccionario: 'Diccionario',
  diario_barrio: 'Diario de barrio',
  expectativa: 'Expectativa',
  lanzamiento: 'Lanzamiento',
  retencion: 'Retención',
  otro: 'Otro',
}

function diasHasta(target, ref = new Date()) {
  const a = new Date(target.getFullYear(), target.getMonth(), target.getDate())
  const b = new Date(ref.getFullYear(), ref.getMonth(), ref.getDate())
  return Math.round((a - b) / 86400000)
}

function rotuloDDay(fechaISO) {
  const f = new Date(fechaISO + 'T12:00:00-03:00')
  const delta = diasHasta(D_DAY, f)
  if (delta === 0) return { texto: 'D-DAY', esDay: true }
  if (delta > 0) return { texto: `D-${delta}`, esDay: false }
  return { texto: `D+${Math.abs(delta)}`, esDay: false }
}

function fechaCorta(iso) {
  const f = new Date(iso + 'T12:00:00-03:00')
  return {
    dia: ['DOM', 'LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB'][f.getDay()],
    num: f.getDate().toString().padStart(2, '0'),
    mes: ['ene', 'feb', 'mar', 'abr', 'may', 'jun', 'jul', 'ago', 'sep', 'oct', 'nov', 'dic'][f.getMonth()],
  }
}

// Parser robusto de campos JSONB que a veces vienen como string desde Supabase
function parseJsonbArray(val) {
  if (!val) return []
  if (Array.isArray(val)) return val
  if (typeof val === 'string') {
    try {
      const p = JSON.parse(val)
      return Array.isArray(p) ? p : []
    } catch { return [] }
  }
  if (typeof val === 'object') return Object.values(val)
  return []
}

function parseJsonbObject(val) {
  if (!val) return null
  if (typeof val === 'object' && !Array.isArray(val)) return val
  if (typeof val === 'string') {
    try { return JSON.parse(val) } catch { return null }
  }
  return null
}

function limpiarResumen(texto) {
  if (!texto) return ''
  return texto
    .replace(/```json[\s\S]*?```/gi, '')
    .replace(/```[\s\S]*?```/g, '')
    .replace(/\{[\s\S]*"resumen"[\s\S]*?\}/g, '')
    .replace(/---+/g, '')
    .replace(/##[^\n]*/g, '')
    .replace(/\*\*([^*]+)\*\*/g, '$1')
    .trim()
}

export default function MarketingHub() {
  const [loading, setLoading] = useState(true)
  const [calendario, setCalendario] = useState([])
  const [posts, setPosts] = useState([])
  const [stats, setStats] = useState({ anclajes: 0, esperan_ok: 0, aprobadas: 0, publicadas_7d: 0 })
  const [generando, setGenerando] = useState(false)
  const [feedback, setFeedback] = useState(null)
  const [cronStatus, setCronStatus] = useState(null)
  const [previewPostId, setPreviewPostId] = useState(null)
  // Modales inline (13-jul · UX simplificada: no redirigir a otra pantalla para acciones básicas)
  const [modalAnclaje, setModalAnclaje] = useState(false)
  const [modalPieza, setModalPieza] = useState(false)
  const [modalBriefIA, setModalBriefIA] = useState(false)
  // Equipo de agentes (13-jul · panel unificado)
  const [equipo, setEquipo] = useState([])

  useEffect(() => { cargar() }, [])

  async function cargar() {
    setLoading(true)
    try {
      const hoy = new Date()
      const hoyStr = hoy.toISOString().slice(0, 10)
      const en7 = new Date(hoy.getTime() + 7 * 86400000).toISOString().slice(0, 10)
      const hace7 = new Date(hoy.getTime() - 7 * 86400000).toISOString()

      // Calendario próximos 7 días
      const { data: cal } = await supabase
        .from('calendario_marketing')
        .select('id, fecha, hora_sugerida, canal, titulo, estado')
        .gte('fecha', hoyStr)
        .lte('fecha', en7)
        .order('fecha', { ascending: true })
        .order('hora_sugerida', { ascending: true, nullsFirst: false })
      setCalendario(cal || [])

      // Todas las piezas activas con horario en los próximos 7 días
      const { data: ps } = await supabase
        .from('posts_ig')
        .select('id, brief, pilar, formato, estado, caption, slides, hashtags, imagenes_renders, horario_sugerido, score_gerente, calendario_id, created_at, agente_owner, redes')
        .in('estado', ['generado', 'aprobado', 'programado', 'publicando', 'publicado'])
        .gte('horario_sugerido', hoyStr)
        .lte('horario_sugerido', en7 + 'T23:59:59')
        .order('horario_sugerido', { ascending: true })
      setPosts(ps || [])

      // Stats globales
      const { count: esperanOK } = await supabase
        .from('posts_ig')
        .select('id', { count: 'exact', head: true })
        .eq('estado', 'generado')

      const { count: aprobadas } = await supabase
        .from('posts_ig')
        .select('id', { count: 'exact', head: true })
        .eq('estado', 'aprobado')

      const { count: publicadas7d } = await supabase
        .from('posts_ig')
        .select('id', { count: 'exact', head: true })
        .eq('estado', 'publicado')
        .gte('publicado_en', hace7)

      setStats({
        anclajes: (cal || []).length,
        esperan_ok: esperanOK || 0,
        aprobadas: aprobadas || 0,
        publicadas_7d: publicadas7d || 0,
      })

      // Cron status: última pieza generada por el gerente (cualquier versión).
      // Fix 26-jul: antes filtraba 'gerente-marketing-auto' (valor que nadie escribe;
      // el gerente inserta 'gerente-marketing-v25/v26') → siempre decía "Sin corridas".
      const { data: cron } = await supabase
        .from('posts_ig')
        .select('created_at')
        .like('agente_owner', 'gerente-marketing%')
        .order('created_at', { ascending: false })
        .limit(1)
      setCronStatus({ ultima: cron?.[0]?.created_at || null })

      // Equipo de agentes: métricas últimos 30 días desde agentes_runs
      const hace30 = new Date(Date.now() - 30 * 86400000).toISOString()
      const { data: runsAg } = await supabase
        .from('agentes_runs')
        .select('agente, rol, tokens_input, tokens_output, costo_usd, latencia_ms, exito')
        .gte('created_at', hace30)
      const porAgente = new Map()
      const ROLES_ORDEN = { gerente: 1, copywriter: 2, disenador: 3, community_manager: 4, analista: 5 }
      const META = {
        'gerente-marketing':      { emoji: '👔', nombre: 'Gerente',         modelo: 'Sonnet 4.6' },
        'copywriter-marketing':   { emoji: '✍️', nombre: 'Copywriter',      modelo: 'Sonnet 4.6' },
        'disenador-marketing':    { emoji: '🎨', nombre: 'Diseñador',       modelo: 'Sonnet 4.6' },
        'community-manager':      { emoji: '🎯', nombre: 'Community Mgr.',  modelo: 'Haiku 4.5' },
        'analista-performance':   { emoji: '📊', nombre: 'Analista',        modelo: 'Haiku 4.5' },
      }
      // Semilla: los 5 agentes aparecen siempre aunque tengan 0 runs
      Object.entries(META).forEach(([slug, m]) => {
        porAgente.set(slug, { slug, ...m, rol: ROLES_ORDEN[Object.keys(ROLES_ORDEN).find(k => slug.includes(k)) || 'otro'] || 99, runs: 0, costo: 0, fallos: 0, latencia_total: 0, tokens: 0 })
      })
      ;(runsAg || []).forEach(r => {
        const key = r.agente
        if (!porAgente.has(key)) return
        const acc = porAgente.get(key)
        acc.runs++
        acc.costo += Number(r.costo_usd || 0)
        acc.latencia_total += Number(r.latencia_ms || 0)
        acc.tokens += Number(r.tokens_input || 0) + Number(r.tokens_output || 0)
        if (!r.exito) acc.fallos++
      })
      const equipoArr = Array.from(porAgente.values())
        .map(a => ({ ...a, latencia_avg: a.runs > 0 ? Math.round(a.latencia_total / a.runs) : 0, costo: Number(a.costo.toFixed(4)) }))
        .sort((a, b) => a.rol - b.rol)
      setEquipo(equipoArr)

    } catch (e) {
      console.error('Hub cargar:', e)
    } finally {
      setLoading(false)
    }
  }

  async function generarAutomaticas() {
    if (!confirm('¿Generar propuestas para toda la semana?\n\nEl Gerente (Sonnet 4.6) lee calendario + últimos posts + rechazos previos y genera hasta 7 piezas para los próximos 7 días. Toma 1-3 minutos.\n\nDespués vos aprobás o rechazás cada una.')) return
    setGenerando(true)
    setFeedback(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token
      if (!token) throw new Error('Sin sesión activa.')
      const res = await fetch(`${SUPA_URL}/functions/v1/gerente-marketing`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ auto: true, cantidad_max: 7, modo: 'semana' }),
      })
      const json = await res.json()
      if (!res.ok || !json.success) throw new Error(json.error || 'Error del agente')
      setFeedback({
        tipo: 'ok',
        cant: json.piezas_creadas?.length || 0,
        modelo: json.modelo_gerente || 'Claude',
        resumen: limpiarResumen(json.resumen).slice(0, 240),
      })
      await cargar()
    } catch (e) {
      setFeedback({ tipo: 'err', resumen: e.message })
    } finally {
      setGenerando(false)
    }
  }

  async function aprobar(id) {
    const { data: { user } } = await supabase.auth.getUser()
    if (!user) { alert('Sesión expirada'); return }
    const { error } = await supabase
      .from('posts_ig')
      .update({
        estado: 'aprobado',
        approved_at: new Date().toISOString(),
        approved_by: user.id,
      })
      .eq('id', id)
    if (error) { alert('Error: ' + error.message); return }
    await cargar()
  }

  async function rechazar(id) {
    const motivo = prompt('¿Por qué rechazás? (queda guardado para que el Gerente aprenda)')
    if (motivo === null) return
    const { data: actual } = await supabase
      .from('posts_ig').select('feedback_gerente').eq('id', id).single()
    const feedbackPrev = actual?.feedback_gerente || []
    const { error } = await supabase
      .from('posts_ig')
      .update({
        estado: 'rechazado',
        feedback_gerente: [...feedbackPrev, motivo || '(sin motivo)'],
      })
      .eq('id', id)
    if (error) { alert('Error: ' + error.message); return }
    await cargar()
  }

  // Agrupar piezas por fecha del horario_sugerido
  const postsPorFecha = useMemo(() => {
    const map = new Map()
    posts.forEach(p => {
      if (!p.horario_sugerido) return
      const fecha = p.horario_sugerido.slice(0, 10)
      if (!map.has(fecha)) map.set(fecha, [])
      map.get(fecha).push(p)
    })
    return map
  }, [posts])

  // Grilla de 7 días empezando HOY
  const dias7 = useMemo(() => {
    const hoy = new Date()
    return Array.from({ length: 7 }, (_, i) => {
      const f = new Date(hoy.getTime() + i * 86400000)
      const iso = f.toISOString().slice(0, 10)
      const anclajes = calendario.filter(c => c.fecha === iso)
      const piezas = postsPorFecha.get(iso) || []
      return { iso, anclajes, piezas }
    })
  }, [calendario, postsPorFecha])

  // Cola de aprobación: piezas en estado='generado'
  const colaAprobacion = useMemo(() => {
    return posts.filter(p => p.estado === 'generado')
      .sort((a, b) => (a.horario_sugerido || '').localeCompare(b.horario_sugerido || ''))
  }, [posts])

  // Listas para publicar: piezas en estado='aprobado'
  const listasPublicar = useMemo(() => {
    return posts.filter(p => p.estado === 'aprobado')
      .sort((a, b) => (a.horario_sugerido || '').localeCompare(b.horario_sugerido || ''))
  }, [posts])

  // Tab activo de la cola (esperan OK vs listas para publicar)
  const [colaTab, setColaTab] = useState('esperan_ok')

  // Publicar ahora una pieza aprobada — compone imagen final (foto + título hero
  // quemado) y llama ig-publicar-post. Publish inmediato (sin scheduling).
  async function publicarAhora(id) {
    const post = posts.find(p => p.id === id)
    if (!post) { alert('Pieza no encontrada'); return }
    if (!confirm(`¿Publicar "${(parseJsonbArray(post.slides)[0]?.titulo) || 'esta pieza'}" en Instagram AHORA?\n\nEsto compone la imagen con el título quemado y la publica inmediatamente. NO se puede deshacer.`)) return
    try {
      // 1. Componer imagen final (foto + título hero por CSS quemado en pixel)
      const finalUrl = await componerImagenFinal(post)

      // 2. Llamar edge fn ig-publicar-post con la imagen compuesta
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token
      const res = await fetch(`${SUPA_URL}/functions/v1/ig-publicar-post`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ post_id: id, image_url_override: finalUrl }),
      })
      const json = await res.json()
      if (!res.ok || json.error) {
        alert('Error: ' + (json.error || 'no se pudo publicar'))
        return
      }
      alert('¡Publicada! ' + (json.permalink || json.ig_media_id))
      await cargar()
    } catch (e) {
      alert('Error: ' + e.message)
    }
  }

  const dDelta = diasHasta(D_DAY)
  const dRotulo = dDelta > 0 ? `D-${dDelta} · jue 18-jun` : ''  // post-lanzamiento: sin rótulo D+X (irrelevante)

  const previewPost = previewPostId ? posts.find(p => p.id === previewPostId) : null
  const hayPreview = !!previewPost

  return (
    <AdminLayout title="Marketing Hub">
      <div className={`mh2-wrap ${hayPreview ? 'mh2-wrap-split' : ''}`}>
        <div className="mh2-main">

        {/* HEADER COMPACTO */}
        <header className="mh2-head">
          <div className="mh2-head-left">
            <div className="mh2-head-eyebrow">MARKETING{dRotulo ? ` · ${dRotulo.toUpperCase()}` : ''}</div>
            <h1 className="mh2-head-title">Hub Marketing</h1>
          </div>
          <div className="mh2-head-actions">
            <button onClick={() => setModalAnclaje(true)} className="mh2-btn mh2-btn-ghost">📌 Anclar fecha</button>
            <button onClick={() => setModalPieza(true)} className="mh2-btn mh2-btn-ghost">✍️ Nueva pieza</button>
            <BotonProgramarBatch posts={posts} onDone={cargar} />
            <button onClick={() => setModalBriefIA(true)} disabled={generando} className="mh2-btn mh2-btn-primary">
              {generando ? 'Pensando…' : '✨ Generar con IA'}
            </button>
          </div>
        </header>

        {/* FEEDBACK */}
        {feedback?.tipo === 'ok' && (
          <div className="mh2-fb mh2-fb-ok">
            <strong>{feedback.cant} propuesta{feedback.cant !== 1 ? 's' : ''} generada{feedback.cant !== 1 ? 's' : ''}</strong>
            <span className="mh2-fb-meta">con {feedback.modelo}</span>
            {feedback.resumen && <div className="mh2-fb-resumen">{feedback.resumen}</div>}
            <button onClick={() => setFeedback(null)} className="mh2-fb-x">×</button>
          </div>
        )}
        {feedback?.tipo === 'err' && (
          <div className="mh2-fb mh2-fb-err">
            <strong>Error:</strong> {feedback.resumen}
            <button onClick={() => setFeedback(null)} className="mh2-fb-x">×</button>
          </div>
        )}

        {/* 🧠 PLAN SEMANAL DEL ESTRATEGA (26-jul-2026) · aprobar la semana en 1 click */}
        <PlanSemanalSection onChanged={cargar} />

        {/* 🎯 META ADS · tile de acceso rápido al panel dedicado (14-jul-2026) */}
        <Link to="/admin/marketing/meta-ads" className="mh2-mads-tile">
          <div className="mh2-mads-tile-left">
            <div className="mh2-mads-tile-icon">
              <svg width="28" height="28" viewBox="0 0 24 24" fill="none">
                <path d="M12 2L2 22h20L12 2z" fill="#00A8B5"/>
                <path d="M12 8l-4 8h8l-4-8z" fill="white"/>
              </svg>
            </div>
            <div>
              <div className="mh2-mads-tile-title">Meta Ads · Panel completo</div>
              <div className="mh2-mads-tile-sub">Campañas · KPIs · CPA real · sugerencias del agente</div>
            </div>
          </div>
          <div className="mh2-mads-tile-caret">→</div>
        </Link>

        {/* 👥 EQUIPO MARKETING · cards de 5 agentes con métricas 30d */}
        <section className="mh2-equipo">
          <div className="mh2-equipo-head">
            <h2>👥 Equipo Marketing</h2>
            <span className="mh2-equipo-sub">Métricas últimos 30 días</span>
          </div>
          <div className="mh2-equipo-grid">
            {equipo.map(a => (
              <div key={a.slug} className={`mh2-agente ${a.fallos > 0 ? 'mh2-agente-err' : ''}`}>
                <div className="mh2-agente-head">
                  <span className="mh2-agente-emoji">{a.emoji}</span>
                  <div>
                    <div className="mh2-agente-nombre">{a.nombre}</div>
                    <div className="mh2-agente-modelo">{a.modelo}</div>
                  </div>
                </div>
                <div className="mh2-agente-stats">
                  <div className="mh2-agente-stat">
                    <span className="mh2-agente-stat-num">{a.runs}</span>
                    <span className="mh2-agente-stat-lbl">runs</span>
                  </div>
                  <div className="mh2-agente-stat">
                    <span className="mh2-agente-stat-num">${a.costo.toFixed(3)}</span>
                    <span className="mh2-agente-stat-lbl">costo</span>
                  </div>
                  <div className="mh2-agente-stat">
                    <span className="mh2-agente-stat-num">{a.latencia_avg > 0 ? `${(a.latencia_avg / 1000).toFixed(1)}s` : '—'}</span>
                    <span className="mh2-agente-stat-lbl">latencia</span>
                  </div>
                </div>
                {a.fallos > 0 && (
                  <div className="mh2-agente-err-badge">⚠️ {a.fallos} fallo{a.fallos !== 1 ? 's' : ''}</div>
                )}
              </div>
            ))}
          </div>
        </section>

        {/* KPIs · 4 columnas */}
        <div className="mh2-kpis">
          <KPI titulo="CALENDARIO 7 DÍAS" valor={stats.anclajes} sub="anclajes activos" color="#1A3461" />
          <KPI titulo="GENERADAS · ESPERAN OK" valor={stats.esperan_ok} sub={stats.esperan_ok > 0 ? 'para revisar y aprobar' : 'todo al día'} color="#00A8B5" highlight={stats.esperan_ok > 0} />
          <KPI titulo="APROBADAS" valor={stats.aprobadas} sub="listas para publicar" color="#10B981" />
          <KPI titulo="PUBLICADAS · 7D" valor={stats.publicadas_7d} sub={stats.publicadas_7d === 0 ? 'no se subió nada aún' : 'últimos 7 días'} color="#6B7280" />
        </div>

        {/* TIMELINE 7 DÍAS */}
        <section className="mh2-card">
          <div className="mh2-card-head">
            <h2 className="mh2-card-title">Timeline · próximos 7 días</h2>
            <div className="mh2-legend">
              <span><i style={{ background: '#10B981' }} /> aprobada</span>
              <span><i style={{ background: '#00A8B5' }} /> generada</span>
              <span><i style={{ background: '#D1D5DB' }} /> sin pieza</span>
            </div>
          </div>
          <div className="mh2-timeline">
            {dias7.map(d => (
              <DiaCard
                key={d.iso}
                dia={d}
                onSeleccionarPieza={(id) => setPreviewPostId(id === previewPostId ? null : id)}
                previewPostId={previewPostId}
              />
            ))}
          </div>
        </section>

        {/* COLA — con tabs Esperan OK / Listas para publicar */}
        <section className="mh2-card">
          <div className="mh2-card-head">
            <div className="mh2-tabs">
              <button
                onClick={() => setColaTab('esperan_ok')}
                className={`mh2-tab ${colaTab === 'esperan_ok' ? 'active' : ''}`}
              >
                Esperan OK
                {colaAprobacion.length > 0 && <span className="mh2-badge-count">{colaAprobacion.length}</span>}
              </button>
              <button
                onClick={() => setColaTab('listas')}
                className={`mh2-tab ${colaTab === 'listas' ? 'active' : ''}`}
              >
                Listas para publicar
                {listasPublicar.length > 0 && <span className="mh2-badge-count mh2-badge-green">{listasPublicar.length}</span>}
              </button>
            </div>
          </div>

          {loading && <div className="mh2-empty-mini">Cargando…</div>}

          {!loading && colaTab === 'esperan_ok' && colaAprobacion.length === 0 && (
            <div className="mh2-empty">
              <strong>Nada pendiente.</strong>
              <p>Cuando el Gerente genere propuestas, aparecen acá.</p>
            </div>
          )}
          {!loading && colaTab === 'esperan_ok' && colaAprobacion.length > 0 && (
            <div className="mh2-cola">
              {colaAprobacion.map(p => (
                <ColaCard
                  key={p.id}
                  post={p}
                  activo={previewPostId === p.id}
                  onPreview={() => setPreviewPostId(p.id === previewPostId ? null : p.id)}
                  onAprobar={() => aprobar(p.id)}
                  onRechazar={() => rechazar(p.id)}
                />
              ))}
            </div>
          )}

          {!loading && colaTab === 'listas' && listasPublicar.length === 0 && (
            <div className="mh2-empty">
              <strong>Nada aprobado todavía.</strong>
              <p>Aprobá una pieza desde "Esperan OK" y aparecerá acá lista para publicar.</p>
            </div>
          )}
          {!loading && colaTab === 'listas' && listasPublicar.length > 0 && (
            <div className="mh2-cola">
              {listasPublicar.map(p => (
                <ListaPublicarCard
                  key={p.id}
                  post={p}
                  activo={previewPostId === p.id}
                  onPreview={() => setPreviewPostId(p.id === previewPostId ? null : p.id)}
                  onPublicar={() => publicarAhora(p.id)}
                />
              ))}
            </div>
          )}
        </section>

        {/* FOOTER STATUS */}
        <div className="mh2-footer">
          <div className="mh2-status">
            <div className="mh2-status-label">CRON DIARIO · agente-marketing-diario</div>
            <div className="mh2-status-value">
              <span className="mh2-dot mh2-dot-ok" /> Activo · 21:00 Mvd
            </div>
            <div className="mh2-status-meta">
              {cronStatus?.ultima
                ? `Última pieza auto: ${new Date(cronStatus.ultima).toLocaleString('es-UY', { day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })}`
                : 'Sin corridas registradas aún'}
            </div>
          </div>
          <div className="mh2-status">
            <div className="mh2-status-label">GUARDRAIL · publicación</div>
            <div className="mh2-status-value">
              <span className="mh2-dot mh2-dot-gold" /> Sólo aprobado_por explícito
            </div>
            <div className="mh2-status-meta">Nada se sube a IG sin tu click en "Aprobar". Aprobar = programar: el publisher la sube solo cuando llega su horario (cron 15 min).</div>
          </div>
        </div>

        </div>
        {hayPreview && (
          <PreviewPanel
            post={previewPost}
            onCerrar={() => setPreviewPostId(null)}
            onAprobar={() => { aprobar(previewPost.id); setPreviewPostId(null) }}
            onRechazar={() => { rechazar(previewPost.id); setPreviewPostId(null) }}
            onGuardado={cargar}
          />
        )}
      </div>

      {/* Modales inline: no redirigen a otra pantalla */}
      {modalAnclaje && <ModalAnclaje onClose={() => setModalAnclaje(false)} onSaved={() => { setModalAnclaje(false); cargar() }} />}
      {modalPieza && <ModalNuevaPieza calendario={calendario} onClose={() => setModalPieza(false)} onSaved={() => { setModalPieza(false); cargar() }} />}
      {modalBriefIA && <ModalBriefIA onClose={() => setModalBriefIA(false)} onGenerado={() => { setModalBriefIA(false); cargar() }} />}

      <Estilos />
      <style>{LIMPACK_CARDS_CSS}</style>
    </AdminLayout>
  )
}

// ── Modal: anclar fecha en calendario_marketing ────────────────
function ModalAnclaje({ onClose, onSaved }) {
  const hoy = new Date().toISOString().slice(0, 10)
  const [form, setForm] = useState({ fecha: hoy, hora: '10:00', titulo: '', canal: 'instagram_post' })
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  async function guardar() {
    if (!form.titulo.trim()) { setErr('Poné un título'); return }
    setSaving(true); setErr(null)
    try {
      const { error } = await supabase.from('calendario_marketing').insert({
        fecha: form.fecha,
        hora_sugerida: form.hora + ':00',
        titulo: form.titulo.trim(),
        canal: form.canal,
        estado: 'pendiente',
      })
      if (error) throw error
      onSaved()
    } catch (e) {
      setErr(e.message || String(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <ModalWrapper onClose={onClose} titulo="📌 Anclar fecha">
      <div className="mh2-modal-row">
        <label>Fecha</label>
        <input type="date" value={form.fecha} onChange={e => setForm({ ...form, fecha: e.target.value })} min={hoy} />
      </div>
      <div className="mh2-modal-row">
        <label>Hora</label>
        <input type="time" value={form.hora} onChange={e => setForm({ ...form, hora: e.target.value })} />
      </div>
      <div className="mh2-modal-row">
        <label>Título</label>
        <input type="text" value={form.titulo} onChange={e => setForm({ ...form, titulo: e.target.value })} placeholder="Ej: Lanzamiento tienda · Feria domingo · Promo suscriptor…" autoFocus />
      </div>
      <div className="mh2-modal-row">
        <label>Canal</label>
        <select value={form.canal} onChange={e => setForm({ ...form, canal: e.target.value })}>
          <optgroup label="Instagram">
            <option value="instagram_post">📷 Post feed</option>
            <option value="instagram_story">⚡ Story</option>
            <option value="instagram_reel">🎬 Reel</option>
          </optgroup>
          <optgroup label="WhatsApp">
            <option value="whatsapp_dm">💬 DM directo</option>
            <option value="whatsapp_grupo">👥 Grupo</option>
          </optgroup>
          <optgroup label="Otros">
            <option value="meta_ads">💰 Meta Ads</option>
            <option value="email">📧 Email</option>
            <option value="volante_fisico">📄 Volante físico</option>
            <option value="visita_edificio">🏢 Visita edificio</option>
            <option value="prensa">📰 Prensa</option>
            <option value="otro">🔖 Otro</option>
          </optgroup>
        </select>
      </div>
      {err && <div className="mh2-modal-err">⚠️ {err}</div>}
      <div className="mh2-modal-foot">
        <button onClick={onClose} className="mh2-btn mh2-btn-ghost">Cancelar</button>
        <button onClick={guardar} disabled={saving} className="mh2-btn mh2-btn-primary">
          {saving ? 'Guardando…' : 'Anclar'}
        </button>
      </div>
    </ModalWrapper>
  )
}

// ── Modal: nueva pieza manual (sin IA) ─────────────────────────
function ModalNuevaPieza({ calendario, onClose, onSaved }) {
  const hoy = new Date().toISOString().slice(0, 10)
  const [form, setForm] = useState({
    brief: '',
    formato: 'feed',
    fecha: hoy,
    hora: '10:00',
    calendario_id: '',
    canal: 'instagram_post',
    pilar: 'diario_barrio',
  })
  const [saving, setSaving] = useState(false)
  const [err, setErr] = useState(null)

  async function guardar() {
    if (!form.brief.trim()) { setErr('Escribí un brief mínimo'); return }
    setSaving(true); setErr(null)
    try {
      const horario = `${form.fecha}T${form.hora}:00`
      const { error } = await supabase.from('posts_ig').insert({
        brief: form.brief.trim(),
        formato: form.formato,
        pilar: form.pilar,
        estado: 'aprobado',   // manual = ya aprobado (Nico lo escribió a mano)
        horario_sugerido: horario,
        calendario_id: form.calendario_id || null,
        redes: [form.canal],
        aprobado_por: 'nico_manual',
        agente_owner: 'nico_manual',
      })
      if (error) throw error
      onSaved()
    } catch (e) {
      setErr(e.message || String(e))
    } finally {
      setSaving(false)
    }
  }

  return (
    <ModalWrapper onClose={onClose} titulo="✍️ Nueva pieza manual">
      <div className="mh2-modal-row">
        <label>Brief · ¿qué querés decir?</label>
        <textarea rows={4} value={form.brief} onChange={e => setForm({ ...form, brief: e.target.value })} placeholder="Ej: Somos una startup uruguaya de suscripción mensual de productos de limpieza. Contá la historia de por qué elegimos Cottonmax…" autoFocus />
      </div>
      <div className="mh2-modal-row-2col">
        <div>
          <label>Formato</label>
          <select value={form.formato} onChange={e => setForm({ ...form, formato: e.target.value })}>
            <option value="feed">Feed (4:5)</option>
            <option value="carrusel">Carrusel</option>
            <option value="story">Story (9:16)</option>
            <option value="reel">Reel (9:16)</option>
            <option value="imagen">Imagen</option>
          </select>
        </div>
        <div>
          <label>Pilar</label>
          <select value={form.pilar} onChange={e => setForm({ ...form, pilar: e.target.value })}>
            <option value="diario_barrio">Diario de barrio</option>
            <option value="costo_invisible">Costo invisible</option>
            <option value="diccionario">Diccionario</option>
            <option value="expectativa">Expectativa</option>
            <option value="lanzamiento">Lanzamiento</option>
            <option value="retencion">Retención</option>
            <option value="otro">Otro</option>
          </select>
        </div>
      </div>
      <div className="mh2-modal-row-2col">
        <div>
          <label>Fecha publicación</label>
          <input type="date" value={form.fecha} onChange={e => setForm({ ...form, fecha: e.target.value })} min={hoy} />
        </div>
        <div>
          <label>Hora</label>
          <input type="time" value={form.hora} onChange={e => setForm({ ...form, hora: e.target.value })} />
        </div>
      </div>
      <div className="mh2-modal-row">
        <label>Anclar a fecha existente (opcional)</label>
        <select value={form.calendario_id} onChange={e => setForm({ ...form, calendario_id: e.target.value })}>
          <option value="">— Sin anclaje —</option>
          {calendario.map(c => (
            <option key={c.id} value={c.id}>{c.fecha} · {c.titulo}</option>
          ))}
        </select>
      </div>
      <div className="mh2-modal-row">
        <label>Canal</label>
        <select value={form.canal} onChange={e => setForm({ ...form, canal: e.target.value })}>
          <optgroup label="Instagram">
            <option value="instagram_post">📷 Post feed</option>
            <option value="instagram_story">⚡ Story</option>
            <option value="instagram_reel">🎬 Reel</option>
          </optgroup>
          <optgroup label="WhatsApp">
            <option value="whatsapp_dm">💬 DM directo</option>
            <option value="whatsapp_grupo">👥 Grupo</option>
          </optgroup>
        </select>
      </div>
      {err && <div className="mh2-modal-err">⚠️ {err}</div>}
      <div className="mh2-modal-foot">
        <button onClick={onClose} className="mh2-btn mh2-btn-ghost">Cancelar</button>
        <button onClick={guardar} disabled={saving} className="mh2-btn mh2-btn-primary">
          {saving ? 'Guardando…' : 'Guardar como aprobada'}
        </button>
      </div>
    </ModalWrapper>
  )
}

// ── Modal: brief libre a IA (llama al Gerente que orquesta el equipo) ──
function ModalBriefIA({ onClose, onGenerado }) {
  const [brief, setBrief] = useState('')
  const [modo, setModo] = useState('brief')  // 'brief' | 'auto'
  const [enviando, setEnviando] = useState(false)
  const [resultado, setResultado] = useState(null)
  const [err, setErr] = useState(null)

  const EJEMPLOS = [
    'Post de lanzamiento oficial. Contraste: perdés tiempo en el súper vs kit a la puerta. Feed. Card_lanzamiento.',
    'Reel de expectativa sobre suscripción mensual. Tono cotidiano.',
    'Story de barrio para Pocitos hoy. Card_barrio.',
    'Post de costo invisible: cuánto gastás por mes en limpieza sin darte cuenta.',
  ]

  async function enviar() {
    if (modo === 'brief' && !brief.trim()) { setErr('Escribí un brief o cambiá a modo AUTO'); return }
    setEnviando(true); setErr(null); setResultado(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token
      if (!token) throw new Error('Sin sesión activa')
      const payload = modo === 'auto' ? { auto: true } : { brief: brief.trim() }
      const r = await fetch(`${SUPA_URL}/functions/v1/gerente-marketing`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify(payload),
      })
      const json = await r.json()
      if (!r.ok || json.error) throw new Error(json.error || `HTTP ${r.status}`)
      setResultado(json)
      // Si generó algo, refresca el Hub (para ver piezas nuevas en "Esperan OK")
      setTimeout(() => onGenerado(), 3000)
    } catch (e) {
      setErr(e.message || String(e))
    } finally {
      setEnviando(false)
    }
  }

  return (
    <ModalWrapper onClose={onClose} titulo="✨ Generar con IA">
      <div className="mh2-modal-row">
        <label>Modo</label>
        <div className="mh2-radio-group">
          <label className={`mh2-radio ${modo === 'brief' ? 'active' : ''}`}>
            <input type="radio" name="modo" value="brief" checked={modo === 'brief'} onChange={() => setModo('brief')} />
            📝 Brief libre
          </label>
          <label className={`mh2-radio ${modo === 'auto' ? 'active' : ''}`}>
            <input type="radio" name="modo" value="auto" checked={modo === 'auto'} onChange={() => setModo('auto')} />
            🤖 Auto (calendario)
          </label>
        </div>
      </div>

      {modo === 'brief' && (
        <>
          <div className="mh2-modal-row">
            <label>Brief para el Gerente</label>
            <textarea
              rows={10}
              value={brief}
              onChange={e => setBrief(e.target.value)}
              placeholder="Ej: Post de lanzamiento oficial. Contraste: perdés 1 hora en el súper + cargás bolsas + gastás más vs Limpack te lleva el kit a la puerta o comprás en la tienda. Feed. Card_lanzamiento. Slides: 1."
              autoFocus
              style={{ fontFamily: '-apple-system, BlinkMacSystemFont, sans-serif' }}
            />
          </div>
          <div className="mh2-modal-row">
            <label>Ejemplos (clickeá para copiar)</label>
            <div style={{ display: 'flex', flexDirection: 'column', gap: 4 }}>
              {EJEMPLOS.map((e, i) => (
                <button key={i} type="button" onClick={() => setBrief(e)} style={{
                  textAlign: 'left', padding: '6px 10px', fontSize: 11.5,
                  background: '#F8F6F1', border: '1px solid #E6E2D8', borderRadius: 6,
                  color: '#5F5E5A', cursor: 'pointer', fontFamily: 'inherit',
                }}>{e}</button>
              ))}
            </div>
          </div>
        </>
      )}

      {modo === 'auto' && (
        <div style={{ padding: 12, background: '#F0FAFB', border: '1px solid #00A8B5', borderRadius: 6, fontSize: 12.5, color: '#005961' }}>
          El Gerente va a leer tu calendario_marketing de los próximos 7 días y generar 1-2 piezas para los anclajes pendientes. Sin brief.
        </div>
      )}

      {err && <div className="mh2-modal-err">⚠️ {err}</div>}
      {resultado && (
        <div style={{ padding: 12, background: '#DCFCE7', border: '1px solid #10B981', borderRadius: 6, fontSize: 12.5, color: '#064E3B', marginTop: 10 }}>
          ✅ {resultado.piezas_creadas?.length || 0} pieza(s) generada(s). Modelo: {resultado.modelo_gerente || '—'}. {resultado.resumen}
          <div style={{ marginTop: 6, fontSize: 11, opacity: 0.8 }}>Refrescando el Hub en 3s…</div>
        </div>
      )}

      <div className="mh2-modal-foot">
        <button onClick={onClose} className="mh2-btn mh2-btn-ghost">{resultado ? 'Cerrar' : 'Cancelar'}</button>
        <button onClick={enviar} disabled={enviando} className="mh2-btn mh2-btn-primary">
          {enviando ? 'El Gerente está pensando…' : '✨ Enviar al Gerente'}
        </button>
      </div>
    </ModalWrapper>
  )
}

// ── Wrapper genérico de modal ──────────────────────────────────
function ModalWrapper({ onClose, titulo, children }) {
  return (
    <div className="mh2-modal-back" onClick={onClose}>
      <div className="mh2-modal" onClick={e => e.stopPropagation()}>
        <div className="mh2-modal-head">
          <h3>{titulo}</h3>
          <button onClick={onClose} className="mh2-modal-x">×</button>
        </div>
        <div className="mh2-modal-body">{children}</div>
      </div>
    </div>
  )
}

// ── PlanSemanalSection · plan del Estratega (Opus 4.8) con aprobación en 1 click ──
// El estratega-semanal corre domingo 20:00 y deja 5-7 anclajes en estado='propuesto'
// + un resumen del porqué. Acá Nico: Aprobar semana (todo → 'pendiente'), borrar filas
// sueltas, o descartar y regenerar. 15 minutos por semana, no 14 aprobaciones sueltas.
const CANAL_MINI = {
  instagram_post: '📷 Post', instagram_story: '⚡ Story', instagram_reel: '🎬 Reel',
  whatsapp_dm: '💬 WA', whatsapp_grupo: '👥 WA', meta_ads: '💰 Ads', email: '📧 Email',
  volante_fisico: '📄 Volante', visita_edificio: '🏢 Visita', prensa: '📰 Prensa', otro: '🔖 Otro',
}

function PlanSemanalSection({ onChanged }) {
  const [plan, setPlan] = useState(null)
  const [filas, setFilas] = useState([])
  const [loading, setLoading] = useState(true)
  const [trabajando, setTrabajando] = useState(false)
  const [msg, setMsg] = useState(null)

  useEffect(() => { cargarPlan() }, [])

  async function cargarPlan() {
    setLoading(true)
    try {
      const { data: p } = await supabase
        .from('planes_semanales')
        .select('id, semana_inicio, resumen, estado, modelo, piezas_propuestas, created_at')
        .eq('estado', 'propuesto')
        .order('created_at', { ascending: false })
        .limit(1)
        .maybeSingle()
      setPlan(p || null)
      if (p) {
        const { data: f } = await supabase
          .from('calendario_marketing')
          .select('id, fecha, hora_sugerida, canal, titulo, copy, notas_internas, estado, url_link')
          .eq('plan_id', p.id)
          .order('fecha', { ascending: true })
          .order('hora_sugerida', { ascending: true })
        setFilas(f || [])
      } else {
        setFilas([])
      }
    } catch (e) {
      console.error('PlanSemanal cargar:', e)
    } finally {
      setLoading(false)
    }
  }

  async function aprobarSemana() {
    if (!plan) return
    if (!confirm(`¿Aprobar el plan de la semana del ${plan.semana_inicio}? (${filas.filter(f => f.estado === 'propuesto').length} piezas)\n\nTodas pasan a "pendiente": el Gerente las produce en sus corridas y vos aprobás cada pieza terminada como siempre. Aprobar pieza = queda programada.`)) return
    setTrabajando(true)
    try {
      const { data, error } = await supabase.rpc('aprobar_plan_semanal', { p_plan_id: plan.id })
      if (error || data?.error) throw new Error(error?.message || data?.error)
      setMsg({ tipo: 'ok', texto: `✓ Semana aprobada · ${data?.piezas_activadas ?? '?'} piezas activadas. El Gerente las va a producir solo.` })
      await cargarPlan()
      if (onChanged) onChanged()
    } catch (e) {
      setMsg({ tipo: 'err', texto: e.message })
    } finally {
      setTrabajando(false)
    }
  }

  async function descartarPlan() {
    if (!plan) return
    if (!confirm('¿Descartar el plan completo? Se borran las filas propuestas. Podés generar otro con "Generar plan ahora".')) return
    setTrabajando(true)
    try {
      const { data, error } = await supabase.rpc('descartar_plan_semanal', { p_plan_id: plan.id })
      if (error || data?.error) throw new Error(error?.message || data?.error)
      setMsg({ tipo: 'ok', texto: 'Plan descartado.' })
      await cargarPlan()
      if (onChanged) onChanged()
    } catch (e) {
      setMsg({ tipo: 'err', texto: e.message })
    } finally {
      setTrabajando(false)
    }
  }

  async function borrarFila(id) {
    if (!confirm('¿Sacar esta pieza del plan?')) return
    const { error } = await supabase.from('calendario_marketing').delete().eq('id', id).eq('estado', 'propuesto')
    if (error) { alert('Error: ' + error.message); return }
    await cargarPlan()
  }

  async function generarAhora() {
    if (!confirm('¿Pedirle al Estratega (Opus 4.8) el plan de la próxima semana AHORA?\n\nLee playbook + métricas + tus rechazos y propone 5-7 piezas. Toma 1-2 minutos. Después vos aprobás la semana.')) return
    setTrabajando(true)
    setMsg(null)
    try {
      const { data: { session } } = await supabase.auth.getSession()
      const token = session?.access_token
      if (!token) throw new Error('Sin sesión activa')
      const res = await fetch(`${SUPA_URL}/functions/v1/estratega-semanal`, {
        method: 'POST',
        headers: { 'Authorization': `Bearer ${token}`, 'Content-Type': 'application/json' },
        body: JSON.stringify({ source: 'hub_manual' }),
      })
      const json = await res.json()
      if (!res.ok || json.error) throw new Error(json.error || `HTTP ${res.status}`)
      setMsg({ tipo: 'ok', texto: json.skip ? json.resumen : `✓ Plan propuesto (${json.piezas_propuestas} piezas, ${json.modelo}).` })
      await cargarPlan()
    } catch (e) {
      setMsg({ tipo: 'err', texto: e.message })
    } finally {
      setTrabajando(false)
    }
  }

  const fmtF = (iso) => {
    const f = new Date(iso + 'T12:00:00-03:00')
    return `${['DOM', 'LUN', 'MAR', 'MIÉ', 'JUE', 'VIE', 'SÁB'][f.getDay()]} ${String(f.getDate()).padStart(2, '0')}`
  }

  return (
    <section className="mh2-card" style={{ borderLeft: '4px solid #C5A059' }}>
      <div className="mh2-card-head">
        <h2 className="mh2-card-title">🧠 Plan semanal del Estratega</h2>
        <div style={{ display: 'flex', gap: 8 }}>
          {plan && (
            <>
              <button onClick={aprobarSemana} disabled={trabajando} className="mh2-btn mh2-btn-primary" style={{ background: '#10B981' }}>
                {trabajando ? '…' : `✓ Aprobar semana (${filas.filter(f => f.estado === 'propuesto').length})`}
              </button>
              <button onClick={descartarPlan} disabled={trabajando} className="mh2-btn mh2-btn-ghost">Descartar</button>
            </>
          )}
          {!plan && !loading && (
            <button onClick={generarAhora} disabled={trabajando} className="mh2-btn mh2-btn-primary">
              {trabajando ? 'El Estratega está pensando…' : '🧠 Generar plan ahora'}
            </button>
          )}
        </div>
      </div>

      {msg && (
        <div style={{
          margin: '0 0 10px', padding: '8px 12px', borderRadius: 6, fontSize: 12.5,
          background: msg.tipo === 'ok' ? '#DCFCE7' : '#FEE2E2',
          color: msg.tipo === 'ok' ? '#064E3B' : '#991B1B',
        }}>
          {msg.texto}
        </div>
      )}

      {loading && <div className="mh2-empty-mini">Cargando plan…</div>}

      {!loading && !plan && (
        <div className="mh2-empty" style={{ padding: 18 }}>
          <strong>Sin plan propuesto.</strong>
          <p style={{ margin: '4px 0 0' }}>El Estratega corre solo los domingos 20:00 y te deja la semana armada acá. También podés pedirla ahora con el botón.</p>
        </div>
      )}

      {!loading && plan && (
        <>
          <div style={{ fontSize: 12.5, color: '#374151', background: '#FBF7EE', border: '1px solid #E9DFC8', borderRadius: 8, padding: '10px 12px', marginBottom: 10, whiteSpace: 'pre-wrap' }}>
            <div style={{ fontSize: 10.5, fontWeight: 800, color: '#8a6d2f', letterSpacing: '.06em', marginBottom: 4 }}>
              SEMANA DEL {plan.semana_inicio} · POR QUÉ ESTE PLAN ({plan.modelo || 'IA'})
            </div>
            {plan.resumen}
          </div>
          <div style={{ display: 'flex', flexDirection: 'column', gap: 6 }}>
            {filas.map(f => (
              <div key={f.id} style={{
                display: 'flex', alignItems: 'center', gap: 10, padding: '8px 10px',
                background: '#F9FAFB', border: '1px solid #E5E7EB', borderRadius: 8, fontSize: 12.5,
              }}>
                <span style={{ fontWeight: 800, color: '#1A3461', minWidth: 58 }}>{fmtF(f.fecha)}</span>
                <span style={{ color: '#6B7280', minWidth: 40 }}>{(f.hora_sugerida || '').slice(0, 5)}</span>
                <span style={{ minWidth: 68, color: '#00666B', fontWeight: 700 }}>{CANAL_MINI[f.canal] || f.canal}</span>
                <span style={{ flex: 1, color: '#111827' }} title={f.copy || ''}>{f.titulo}</span>
                <a href="/admin/calendario-marketing" style={{ fontSize: 11, color: '#00A8B5', fontWeight: 700, textDecoration: 'none' }}>editar</a>
                <button onClick={() => borrarFila(f.id)} title="Sacar del plan" style={{
                  border: 'none', background: 'transparent', color: '#DC2626', cursor: 'pointer', fontWeight: 800, fontSize: 13,
                }}>✕</button>
              </div>
            ))}
          </div>
        </>
      )}
    </section>
  )
}

// ── KPI ───────────────────────────────────────────────────────
function KPI({ titulo, valor, sub, color, highlight }) {
  return (
    <div className={`mh2-kpi ${highlight ? 'mh2-kpi-hl' : ''}`} style={{ borderLeftColor: color }}>
      <div className="mh2-kpi-titulo">{titulo}</div>
      <div className="mh2-kpi-valor" style={{ color }}>{valor}</div>
      <div className="mh2-kpi-sub">{sub}</div>
    </div>
  )
}

// ── DiaCard · una columna del timeline ────────────────────────
function DiaCard({ dia, onSeleccionarPieza, previewPostId }) {
  const f = fechaCorta(dia.iso)
  const sinNada = dia.anclajes.length === 0 && dia.piezas.length === 0
  const cssBorder = dia.piezas.length > 0 ? '#00A8B5' : '#D1D5DB'

  return (
    <div className="mh2-dia" style={{ borderTopColor: cssBorder }}>
      <div className="mh2-dia-head">
        <span className="mh2-dia-dow">{f.dia} {f.num} {f.mes}</span>
        {dia.piezas.length > 0 && <span className="mh2-dia-badge">{dia.piezas.length}</span>}
      </div>

      {/* Anclajes del calendario (con su pieza si existe) */}
      {dia.anclajes.map(a => {
        const pieza = dia.piezas.find(p => p.calendario_id === a.id)
        return (
          <AnclajeItem
            key={a.id}
            anclaje={a}
            pieza={pieza}
            activo={pieza && previewPostId === pieza.id}
            onClick={pieza ? () => onSeleccionarPieza(pieza.id) : undefined}
          />
        )
      })}

      {/* Piezas sin anclaje */}
      {dia.piezas.filter(p => !dia.anclajes.some(a => a.id === p.calendario_id)).map(p => (
        <PiezaItem
          key={p.id}
          pieza={p}
          activo={previewPostId === p.id}
          onClick={() => onSeleccionarPieza(p.id)}
        />
      ))}

      {sinNada && (
        <Link to="/admin/calendario-marketing" className="mh2-dia-empty">+ anclar pieza</Link>
      )}
    </div>
  )
}

function AnclajeItem({ anclaje, pieza, activo, onClick }) {
  const hora = anclaje.hora_sugerida?.slice(0, 5) || '—'
  const formato = pieza?.formato || ''
  const aspect = FORMATO_ASPECT[formato] || '4:5'
  const fmtLabel = FORMATO_LABEL[formato] || formato || 'feed'
  const tieneOK = pieza?.estado === 'aprobado' || pieza?.estado === 'programado'
  const tienePropuesta = pieza?.estado === 'generado'
  const tienePublicado = pieza?.estado === 'publicado'
  let cssBg = '#F1F0EA', cssColor = '#6B7280'
  if (tienePublicado) { cssBg = '#EDE9FE'; cssColor = '#6B21A8' }
  else if (tieneOK)   { cssBg = '#D1FAE5'; cssColor = '#065F46' }
  else if (tienePropuesta) { cssBg = '#E0F4F6'; cssColor = '#00666B' }

  const ClickableTag = onClick ? 'button' : 'div'
  return (
    <ClickableTag
      onClick={onClick}
      className={`mh2-anc ${onClick ? 'mh2-anc-click' : ''} ${activo ? 'mh2-anc-activo' : ''}`}
      style={{ background: cssBg, color: cssColor }}
      title={pieza ? `Click para ver: ${(pieza.brief || '').slice(0, 60)}` : ''}
    >
      <span className="mh2-anc-hora">{hora}</span>
      <span className="mh2-anc-fmt">{fmtLabel} {aspect}</span>
      {tienePublicado && <span className="mh2-anc-pub">✦</span>}
      {tieneOK && !tienePublicado && <span className="mh2-anc-ok">✓</span>}
      {tienePropuesta && <span className="mh2-anc-pend">●</span>}
    </ClickableTag>
  )
}

function PiezaItem({ pieza, activo, onClick }) {
  const fmtLabel = FORMATO_LABEL[pieza.formato] || pieza.formato || 'feed'
  const aspect = FORMATO_ASPECT[pieza.formato] || '4:5'
  const hora = pieza.horario_sugerido
    ? new Date(pieza.horario_sugerido).toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit' })
    : '—'
  return (
    <button
      onClick={onClick}
      className={`mh2-anc mh2-anc-click ${activo ? 'mh2-anc-activo' : ''}`}
      style={{ background: '#FEF3C7', color: '#92400E' }}
      title="Pieza sin anclaje del calendario"
    >
      <span className="mh2-anc-hora">{hora}</span>
      <span className="mh2-anc-fmt">{fmtLabel} {aspect}</span>
      <span title="sin anclaje">⚠</span>
    </button>
  )
}

// ── ListaPublicarCard · card de pieza aprobada lista para publicar ──
function ListaPublicarCard({ post, activo, onPreview, onPublicar }) {
  const horario = post.horario_sugerido ? new Date(post.horario_sugerido) : null
  const fechaTag = horario
    ? `${['DOM','LUN','MAR','MIÉ','JUE','VIE','SÁB'][horario.getDay()]} ${String(horario.getDate()).padStart(2,'0')} · ${horario.toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit' })}`
    : 'Sin horario'
  const fmtLabel = FORMATO_LABEL[post.formato] || post.formato || 'feed'
  const aspect = FORMATO_ASPECT[post.formato] || '4:5'
  const titulo = (post.brief || '').split('\n')[0]?.slice(0, 60) || 'Sin título'
  const rendersObj = parseJsonbObject(post.imagenes_renders) || {}
  // v3 (15-jun-2026): preferimos render CSS (oficial brand v3.2). Ideogram/flux solo fallback.
  const tieneRender = parseJsonbArray(rendersObj.css)[0] || parseJsonbArray(rendersObj.ideogram)[0] || parseJsonbArray(rendersObj.flux)[0]

  return (
    <div className={`mh2-cola-card mh2-lista-card ${activo ? 'mh2-cola-activo' : ''}`}>
      <div className="mh2-cola-head">
        <span className="mh2-cola-fecha" style={{ background: '#10B981' }}>{fechaTag} · APROBADA</span>
        <span className="mh2-cola-fmt">{fmtLabel} {aspect}</span>
      </div>
      <div className="mh2-cola-titulo">{titulo}</div>
      <div className="mh2-lista-render">
        {tieneRender ? '🖼️ Imagen lista para publicar' : '⏳ Render en proceso (~10s)'}
      </div>
      <div className="mh2-cola-acciones">
        <button
          onClick={onPublicar}
          className="mh2-cb mh2-cb-publicar"
          disabled={!tieneRender}
          title={tieneRender ? 'Publicar en Instagram ahora' : 'Esperando render Ideogram'}
        >
          Publicar ahora
        </button>
        <button onClick={onPreview} className="mh2-cb mh2-cb-edit">
          {activo ? 'Cerrar' : 'Ver →'}
        </button>
      </div>
    </div>
  )
}

// ── ColaCard · card compacta en cola de aprobación ───────────
function ColaCard({ post, activo, onPreview, onAprobar, onRechazar }) {
  const horario = post.horario_sugerido ? new Date(post.horario_sugerido) : null
  const fechaTag = horario
    ? `${['DOM','LUN','MAR','MIÉ','JUE','VIE','SÁB'][horario.getDay()]} ${String(horario.getDate()).padStart(2,'0')} · ${horario.toLocaleTimeString('es-UY', { hour: '2-digit', minute: '2-digit' })}`
    : 'Sin horario'
  const fmtLabel = FORMATO_LABEL[post.formato] || post.formato || 'feed'
  const aspect = FORMATO_ASPECT[post.formato] || '4:5'
  const pilarLbl = PILAR_LABEL[post.pilar] || post.pilar || '—'
  const isDDay = horario && diasHasta(D_DAY, horario) === 0
  const titulo = (post.brief || '').split('\n')[0]?.slice(0, 60) || 'Sin título'
  const captionPreview = (post.caption || '').slice(0, 140)

  return (
    <div className={`mh2-cola-card ${isDDay ? 'mh2-cola-dday' : ''} ${activo ? 'mh2-cola-activo' : ''}`}>
      <div className="mh2-cola-head">
        <span className={`mh2-cola-fecha ${isDDay ? 'mh2-cola-fecha-gold' : ''}`}>
          {fechaTag}{isDDay ? ' · D-DAY' : ''}
        </span>
        <span className="mh2-cola-fmt">{fmtLabel} {aspect}</span>
      </div>
      <div className="mh2-cola-pilar">{pilarLbl}</div>
      <div className="mh2-cola-titulo">{titulo}</div>
      {captionPreview && <div className="mh2-cola-cap">{captionPreview}{post.caption?.length > 140 ? '…' : ''}</div>}
      <div className="mh2-cola-acciones">
        <button onClick={onAprobar} className="mh2-cb mh2-cb-ok">Aprobar</button>
        <button onClick={onPreview} className="mh2-cb mh2-cb-edit">
          {activo ? 'Cerrar' : 'Ver →'}
        </button>
        <button onClick={onRechazar} className="mh2-cb mh2-cb-rej" title="Rechazar con feedback">✕</button>
      </div>
    </div>
  )
}

// ── PreviewPanel · panel lateral derecho con mockup celular ──
function PreviewPanel({ post, onCerrar, onAprobar, onRechazar, onGuardado }) {
  const [slideIdx, setSlideIdx] = useState(0)
  // Parsers robustos: Supabase a veces entrega jsonb como string
  const slides = parseJsonbArray(post.slides)
  const slideActual = slides[slideIdx] || slides[0]
  const esStory = ['9:16'].includes(FORMATO_ASPECT[post.formato])
  const horario = post.horario_sugerido ? new Date(post.horario_sugerido) : null
  const fechaTxt = horario
    ? horario.toLocaleString('es-UY', { weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit' })
    : 'Sin horario'
  const rendersObj = parseJsonbObject(post.imagenes_renders) || {}
  const renders = parseJsonbArray(rendersObj.ideogram).length
    ? parseJsonbArray(rendersObj.ideogram)
    : parseJsonbArray(rendersObj.flux)
  const hayImagen = renders.some(u => !!u)

  // Edición inline caption + hashtags (Nico: "quiero leer todo el texto y modificarlo")
  const [captionEdit, setCaptionEdit] = useState(post.caption || '')
  const [hashtagsEdit, setHashtagsEdit] = useState(parseJsonbArray(post.hashtags).join(' '))
  const [guardando, setGuardando] = useState(false)
  const [msgGuardado, setMsgGuardado] = useState('')
  const captionInicial = post.caption || ''
  const hashtagsInicial = parseJsonbArray(post.hashtags).join(' ')
  const cambios = captionEdit !== captionInicial || hashtagsEdit !== hashtagsInicial

  // Reset slideIdx + captions si cambia el post
  useEffect(() => {
    setSlideIdx(0)
    setCaptionEdit(post.caption || '')
    setHashtagsEdit(parseJsonbArray(post.hashtags).join(' '))
    setMsgGuardado('')
  }, [post.id])

  async function guardar() {
    setGuardando(true)
    setMsgGuardado('')
    const hashtagsArr = hashtagsEdit
      .split(/[\s,]+/)
      .map(h => h.trim())
      .filter(Boolean)
      .map(h => h.startsWith('#') ? h : '#' + h)
    const { error } = await supabase
      .from('posts_ig')
      .update({ caption: captionEdit, hashtags: hashtagsArr })
      .eq('id', post.id)
    setGuardando(false)
    if (error) {
      setMsgGuardado('❌ ' + error.message)
    } else {
      setMsgGuardado('✓ Guardado')
      if (onGuardado) onGuardado()
      setTimeout(() => setMsgGuardado(''), 2500)
    }
  }

  return (
    <aside className="mh2-preview">
      <div className="mh2-preview-head">
        <div>
          <div className="mh2-preview-eyebrow">PREVIEW · {fechaTxt}</div>
          <div className="mh2-preview-titulo">{PILAR_LABEL[post.pilar] || post.pilar}</div>
        </div>
        <button onClick={onCerrar} className="mh2-preview-cerrar" title="Cerrar">×</button>
      </div>

      {/* Chassis celular */}
      <div className="mh2-phone">
        <div className="mh2-phone-bezel">
          <div className="mh2-phone-notch" />
          <div className="mh2-phone-screen">
            {/* Status bar */}
            <div className="mh2-phone-sb">
              <span>9:41</span>
              <span className="mh2-phone-sb-icons">⌃</span>
            </div>
            {/* IG header */}
            <div className="mh2-phone-igh">
              <div className="mh2-phone-avatar">L</div>
              <div className="mh2-phone-user">
                <strong>limpackuy</strong>
                <span>Montevideo, Uruguay</span>
              </div>
              <span className="mh2-phone-dots">⋯</span>
            </div>
            {/* Imagen — Nano Banana v4 genera foto pura sin texto; el título va por CSS overlay */}
            <div className={`mh2-phone-img ${esStory ? 'mh2-phone-img-story' : ''}`} style={{ position: 'relative' }}>
              {hayImagen && renders[slideIdx] ? (
                <>
                  <img src={renders[slideIdx]} alt={`slide ${slideIdx + 1}`} />
                  <TituloOverlay slide={slideActual} esStory={esStory} />
                </>
              ) : slideActual ? (
                <IGSlideRender slide={slideActual} esStory={esStory} />
              ) : (
                <div className="mh2-phone-empty">Esta pieza no tiene slides cargados</div>
              )}
              {slides.length > 1 && (
                <div className="mh2-phone-counter">{slideIdx + 1}/{slides.length}</div>
              )}
              {!hayImagen && slideActual && (
                <div className="mh2-phone-overlay-mock" title="Preview CSS — sin render Ideogram aún">PREVIEW</div>
              )}
            </div>
            {/* Slide nav */}
            {slides.length > 1 && (
              <div className="mh2-phone-nav">
                {slides.map((_, i) => (
                  <button
                    key={i}
                    onClick={() => setSlideIdx(i)}
                    className={`mh2-phone-dot ${i === slideIdx ? 'active' : ''}`}
                    aria-label={`slide ${i + 1}`}
                  />
                ))}
              </div>
            )}
            {/* Acciones IG */}
            <div className="mh2-phone-acts">
              <span>♡</span><span>💬</span><span>📤</span><span style={{ marginLeft: 'auto' }}>🔖</span>
            </div>
            {/* Caption preview compacto — solo primeras líneas */}
            {captionEdit && (
              <div className="mh2-phone-cap">
                <strong>limpackuy</strong>{' '}
                {captionEdit.length > 140 ? captionEdit.slice(0, 140) + '…' : captionEdit}
              </div>
            )}
          </div>
        </div>
      </div>

      {/* Editor caption + hashtags */}
      <div style={{ padding: '14px 16px', borderTop: '1px solid #E5E7EB', background: '#F9FAFB' }}>
        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#374151', marginBottom: 5, textTransform: 'uppercase', letterSpacing: '.05em' }}>
          Caption ({captionEdit.length} chars)
        </label>
        <textarea
          value={captionEdit}
          onChange={e => setCaptionEdit(e.target.value)}
          rows={Math.min(14, Math.max(5, captionEdit.split('\n').length + 1))}
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '8px 10px', fontSize: 13, lineHeight: 1.5,
            border: '1px solid #D1D5DB', borderRadius: 6, resize: 'vertical',
            fontFamily: 'inherit', color: '#111827', background: 'white',
          }}
          placeholder="Caption completo…"
        />

        <label style={{ display: 'block', fontSize: 11, fontWeight: 700, color: '#374151', margin: '10px 0 5px', textTransform: 'uppercase', letterSpacing: '.05em' }}>
          Hashtags ({hashtagsEdit.split(/[\s,]+/).filter(Boolean).length})
        </label>
        <textarea
          value={hashtagsEdit}
          onChange={e => setHashtagsEdit(e.target.value)}
          rows={2}
          style={{
            width: '100%', boxSizing: 'border-box',
            padding: '8px 10px', fontSize: 12, lineHeight: 1.5,
            border: '1px solid #D1D5DB', borderRadius: 6, resize: 'vertical',
            fontFamily: 'inherit', color: '#1E3A8A', background: 'white',
          }}
          placeholder="#Limpack #Montevideo #Pocitos …"
        />

        {cambios && (
          <div style={{ display: 'flex', alignItems: 'center', gap: 8, marginTop: 8 }}>
            <button
              onClick={guardar}
              disabled={guardando}
              style={{
                background: '#1A3461', color: 'white', border: 'none',
                padding: '7px 14px', borderRadius: 6, fontSize: 12, fontWeight: 700,
                cursor: guardando ? 'wait' : 'pointer'
              }}
            >
              {guardando ? 'Guardando…' : '💾 Guardar cambios'}
            </button>
            <span style={{ fontSize: 11, color: '#78350F' }}>
              Hay cambios sin guardar
            </span>
          </div>
        )}
        {msgGuardado && (
          <div style={{ marginTop: 8, fontSize: 12, color: msgGuardado.startsWith('✓') ? '#059669' : '#DC2626', fontWeight: 600 }}>
            {msgGuardado}
          </div>
        )}
      </div>

      {/* Preview imagen final (composer canvas) — visible cuando estado='aprobado' */}
      {post.estado === 'aprobado' && (
        <div style={{ padding: '0 16px 8px' }}>
          <BotonPreviewImagen post={post} />
        </div>
      )}

      {/* Acciones */}
      <div className="mh2-preview-acts">
        {post.estado === 'aprobado' ? (
          <BotonPublicarIG post={post} onPublicado={onGuardado} />
        ) : (
          <button
            onClick={onAprobar}
            disabled={cambios}
            title={cambios ? 'Guardá los cambios antes de aprobar' : ''}
            className="mh2-cb mh2-cb-ok"
            style={cambios ? { opacity: 0.5, cursor: 'not-allowed' } : {}}
          >
            Aprobar
          </button>
        )}
        <button onClick={onRechazar} className="mh2-cb mh2-cb-rej-full">Rechazar</button>
      </div>
    </aside>
  )
}

// ── componerImagenFinal · helper compartido entre Previsualizar y Publicar ──
// Genera la imagen final (foto pura + título hero quemado en pixel), sube al bucket
// y devuelve la URL firmada. Extraído para poder reusarse en preview y publish.
async function componerImagenFinal(post) {
  const rendersObj = parseJsonbObject(post.imagenes_renders) || {}
  const fluxArr = parseJsonbArray(rendersObj.flux)
  const baseUrl = fluxArr[0] || rendersObj['0']?.url
  if (!baseUrl) throw new Error('La pieza no tiene render base')

  const slides = parseJsonbArray(post.slides)
  const slide = slides[0] || {}
  const titulo = (slide.titulo || '').trim()
  const esStory = post.formato === 'story' || post.formato === 'reel'

  // 1. Cargar imagen base
  const img = await new Promise((res, rej) => {
    const i = new Image()
    i.crossOrigin = 'anonymous'
    i.onload = () => res(i)
    i.onerror = () => rej(new Error('No se pudo cargar la imagen base'))
    i.src = baseUrl
  })

  const cnv = document.createElement('canvas')
  cnv.width = img.naturalWidth
  cnv.height = img.naturalHeight
  const ctx = cnv.getContext('2d')
  ctx.drawImage(img, 0, 0)

  if (titulo) {
    const w = cnv.width
    const h = cnv.height
    const fontSize = Math.round(w * (esStory ? 0.11 : 0.09))
    const lineHeight = fontSize * (esStory ? 1.0 : 1.05)
    ctx.font = `800 ${fontSize}px "Inter", -apple-system, sans-serif`
    ctx.fillStyle = '#FFFFFF'
    ctx.textAlign = esStory ? 'center' : 'left'
    ctx.textBaseline = 'top'
    ctx.shadowColor = 'rgba(0,0,0,0.45)'
    ctx.shadowBlur = fontSize * 0.4
    ctx.shadowOffsetX = 0
    ctx.shadowOffsetY = fontSize * 0.06

    const marginX = Math.round(w * 0.06)
    const posY = Math.round(h * (esStory ? 0.10 : 0.06))
    const posX = esStory ? w / 2 : marginX
    const maxTextWidth = w - marginX * 2

    const palabras = titulo.split(/\s+/)
    const lineas = []
    let linea = ''
    for (const p of palabras) {
      const prueba = linea ? linea + ' ' + p : p
      if (ctx.measureText(prueba).width > maxTextWidth && linea) {
        lineas.push(linea)
        linea = p
      } else {
        linea = prueba
      }
    }
    if (linea) lineas.push(linea)
    lineas.forEach((ln, i) => {
      ctx.fillText(ln, posX, posY + i * lineHeight)
    })
  }

  const blob = await new Promise((res) => cnv.toBlob(res, 'image/jpeg', 0.92))
  if (!blob) throw new Error('No se pudo exportar el canvas')

  const path = `posts/${post.id}/final-ig-${Date.now()}.jpg`
  const { error: upErr } = await supabase.storage.from('productos-fotos-ml').upload(path, blob, {
    contentType: 'image/jpeg', upsert: true,
  })
  if (upErr) throw upErr
  const { data: signed } = await supabase.storage.from('productos-fotos-ml').createSignedUrl(path, 60 * 60 * 24 * 365)
  const finalUrl = signed?.signedUrl
  if (!finalUrl) throw new Error('No se pudo firmar la URL final')
  return finalUrl
}

// ── BotonProgramarBatch · programa TODAS las aprobadas con horario futuro de un click ──
// Ideal para dejar el calendario listo antes de dormir. Itera cada pieza:
// compone imagen final → sube al bucket → llama ig-publicar-post con scheduled_publish_time.
// Stories se saltan (IG no permite scheduling nativo de stories).
function BotonProgramarBatch({ posts, onDone }) {
  const [estado, setEstado] = useState('idle') // idle | trabajando | ok | err
  const [progress, setProgress] = useState('')

  // Filtrar: estado='aprobado' + horario_sugerido > ahora+10min + NO story
  const ahoraMs = Date.now()
  const candidatos = (posts || []).filter(p => {
    if (p.estado !== 'aprobado') return false
    if (!p.horario_sugerido) return false
    const t = new Date(p.horario_sugerido).getTime()
    if (t < ahoraMs + 10 * 60 * 1000) return false
    if (p.formato === 'story' || p.formato === 'reel') return false
    return true
  })

  if (candidatos.length === 0 && estado === 'idle') return null

  async function programarTodas() {
    setEstado('trabajando')
    const resultados = []
    for (let i = 0; i < candidatos.length; i++) {
      const p = candidatos[i]
      const titulo = ((parseJsonbArray(p.slides)[0]?.titulo) || '').slice(0, 30)
      setProgress(`${i + 1}/${candidatos.length} · ${titulo}`)
      try {
        const finalUrl = await componerImagenFinal(p)
        const { data: sess } = await supabase.auth.getSession()
        const jwt = sess?.session?.access_token
        const supaUrl = supabase.supabaseUrl
        const r = await fetch(`${supaUrl}/functions/v1/ig-publicar-post`, {
          method: 'POST',
          headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${jwt}` },
          body: JSON.stringify({
            post_id: p.id,
            image_url_override: finalUrl,
            scheduled_publish_time: Math.floor(new Date(p.horario_sugerido).getTime() / 1000),
          }),
        })
        const j = await r.json()
        if (!j.success) throw new Error(j.error || 'edge fn falló')
        resultados.push({ id: p.id, titulo, ok: true, scheduled_for: j.scheduled_for })
      } catch (e) {
        resultados.push({ id: p.id, titulo, ok: false, error: e.message || String(e) })
      }
    }
    const okCount = resultados.filter(r => r.ok).length
    const errCount = resultados.length - okCount
    setEstado(errCount === 0 ? 'ok' : 'err')
    setProgress(`${okCount}/${resultados.length} programadas${errCount ? ` · ${errCount} fallaron` : ''}`)
    if (onDone) onDone()
    // Log errores en consola
    resultados.filter(r => !r.ok).forEach(r => console.error(`[ProgramarBatch] ${r.titulo}:`, r.error))
  }

  return (
    <button
      onClick={programarTodas}
      disabled={estado === 'trabajando' || candidatos.length === 0}
      className="mh2-btn"
      style={{
        background: estado === 'ok' ? '#059669' : estado === 'err' ? '#DC2626' : '#E4405F',
        color: 'white', fontWeight: 700,
        cursor: estado === 'trabajando' ? 'wait' : 'pointer',
      }}
      title={candidatos.map(p => (parseJsonbArray(p.slides)[0]?.titulo) || '').join(' · ')}
    >
      {estado === 'idle' && `📅 Programar ${candidatos.length} en IG`}
      {estado === 'trabajando' && `⏳ ${progress}`}
      {estado === 'ok' && `✓ ${progress}`}
      {estado === 'err' && `⚠ ${progress} · ver consola`}
    </button>
  )
}

// ── BotonPreviewImagen · genera la imagen final y la abre en pestaña nueva ──
function BotonPreviewImagen({ post }) {
  const [estado, setEstado] = useState('idle')
  const [msg, setMsg] = useState('')

  async function ver() {
    setEstado('trabajando')
    setMsg('Armando imagen final...')
    try {
      const url = await componerImagenFinal(post)
      window.open(url, '_blank', 'noopener,noreferrer')
      setEstado('ok')
      setMsg('✓ Abierta en pestaña nueva')
      setTimeout(() => { setEstado('idle'); setMsg('') }, 3500)
    } catch (e) {
      setEstado('err')
      setMsg('❌ ' + (e.message || String(e)))
    }
  }

  return (
    <div style={{ marginBottom: 8 }}>
      <button
        onClick={ver}
        disabled={estado === 'trabajando'}
        style={{
          width: '100%', padding: '9px 14px', borderRadius: 6,
          border: '1px solid #D1D5DB', background: 'white', color: '#374151',
          fontSize: 12, fontWeight: 600, cursor: estado === 'trabajando' ? 'wait' : 'pointer',
        }}
      >
        {estado === 'idle' && '👁 Previsualizar imagen final (foto + título)'}
        {estado === 'trabajando' && '⏳ ' + msg}
        {estado === 'ok' && msg}
        {estado === 'err' && msg}
      </button>
    </div>
  )
}

// ── BotonPublicarIG · reusa componerImagenFinal + llama ig-publicar-post ──
// Si horario_sugerido > ahora+10min → scheduling nativo Meta (edge fn programa el post).
// Si no → publish inmediato.
function BotonPublicarIG({ post, onPublicado }) {
  const [estado, setEstado] = useState('idle') // idle | componiendo | subiendo | publicando | ok | err
  const [msg, setMsg] = useState('')
  const [permalink, setPermalink] = useState('')

  // ¿Programar o publicar ahora?
  const horarioMs = post.horario_sugerido ? new Date(post.horario_sugerido).getTime() : 0
  const ahoraMs = Date.now()
  const esProgramable = horarioMs > ahoraMs + 10 * 60 * 1000  // al menos 10 min en el futuro
  const esStory = post.formato === 'story' || post.formato === 'reel'
  const puedeProgramarse = esProgramable && !esStory  // IG no permite scheduling nativo de Stories
  const horarioTxt = horarioMs ? new Date(horarioMs).toLocaleString('es-UY', {
    weekday: 'short', day: '2-digit', month: 'short', hour: '2-digit', minute: '2-digit'
  }) : ''

  async function componerYPublicar() {
    setEstado('componiendo')
    setMsg('Armando imagen final...')
    try {
      const finalUrl = await componerImagenFinal(post)

      // Invocar edge fn ig-publicar-post (scheduling o publish inmediato)
      setEstado('publicando')
      setMsg(puedeProgramarse ? `Programando en Instagram para ${horarioTxt}...` : 'Publicando en Instagram...')
      const { data: sess } = await supabase.auth.getSession()
      const jwt = sess?.session?.access_token
      const supaUrl = supabase.supabaseUrl
      const payload = { post_id: post.id, image_url_override: finalUrl }
      if (puedeProgramarse) payload.scheduled_publish_time = Math.floor(horarioMs / 1000)
      const r = await fetch(`${supaUrl}/functions/v1/ig-publicar-post`, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json', 'Authorization': `Bearer ${jwt}` },
        body: JSON.stringify(payload),
      })
      const j = await r.json()
      if (!j.success) throw new Error(j.error || 'Error publicando en IG')

      setEstado('ok')
      if (j.scheduled) {
        setMsg(`✓ Programado en Instagram para ${new Date(j.scheduled_for).toLocaleString('es-UY')}`)
      } else {
        setMsg(`✓ Publicado en Instagram · ${j.ig_media_id}`)
        setPermalink(j.permalink || '')
      }
      if (onPublicado) onPublicado()
    } catch (e) {
      setEstado('err')
      setMsg('❌ ' + (e.message || String(e)))
    }
  }

  const enProceso = ['componiendo', 'subiendo', 'publicando'].includes(estado)

  return (
    <div style={{ flex: 1 }}>
      <button
        onClick={componerYPublicar}
        disabled={enProceso || estado === 'ok'}
        className="mh2-cb mh2-cb-ok"
        style={{
          width: '100%',
          background: estado === 'ok' ? '#059669' : '#E4405F',
          color: 'white',
          fontWeight: 700,
          cursor: enProceso ? 'wait' : 'pointer',
        }}
      >
        {estado === 'idle' && (puedeProgramarse ? `📅 Programar en IG · ${horarioTxt}` : '📸 Publicar en Instagram ahora')}
        {enProceso && '⏳ ' + msg}
        {estado === 'ok' && '✓ Listo'}
        {estado === 'err' && '⚠ Reintentar'}
      </button>
      {msg && estado !== 'idle' && (
        <div style={{
          marginTop: 6, fontSize: 11,
          color: estado === 'err' ? '#DC2626' : estado === 'ok' ? '#059669' : '#6B7280',
        }}>
          {msg}
          {permalink && (
            <> · <a href={permalink} target="_blank" rel="noopener noreferrer" style={{ color: '#00A8B5', fontWeight: 700 }}>Ver post</a></>
          )}
        </div>
      )}
    </div>
  )
}

// ── TituloOverlay · texto por CSS sobre foto pura (Nano Banana v4 sin texto) ──
// Nico 2026-07-14: los modelos IA renderean tipografía mal (parten palabras, tracking suelto).
// La foto es limpia + texto por CSS con Inter Bold controlado.
// Sin CTA overlay — el link ya vive en el hero + caption, evita competir con el fondo.
function TituloOverlay({ slide, esStory }) {
  if (!slide) return null
  const titulo = slide.titulo || ''
  if (!titulo.trim()) return null

  const heroSize = esStory ? 46 : 34
  const heroLh = esStory ? 1.0 : 1.05
  const posTop = esStory ? '10%' : '6%'

  return (
    <div style={{
      position: 'absolute',
      top: posTop,
      left: '6%',
      right: '6%',
      fontFamily: '"Inter", -apple-system, sans-serif',
      fontWeight: 800,
      fontSize: heroSize,
      lineHeight: heroLh,
      letterSpacing: '-0.02em',
      color: '#FFFFFF',
      textShadow: '0 2px 12px rgba(0,0,0,0.35), 0 1px 2px rgba(0,0,0,0.4)',
      textAlign: esStory ? 'center' : 'left',
      pointerEvents: 'none',
      wordBreak: 'keep-all',
      overflowWrap: 'break-word',
    }}>
      {titulo}
    </div>
  )
}

// ── TEMPLATE_SPECS + helpers (espejo del Manual de Marca v3.2) ──
const TEMPLATE_SPECS = {
  feed_promo: {
    bg: 'linear-gradient(135deg, #070B14 0%, #0c1220 100%)',
    textColor: '#FFFFFF', eyebrowColor: '#00D4E0', accentColor: '#00D4E0',
    stickerBg: '#FFFFFF', stickerColor: '#1A3461', stickerLabel: 'Promo',
  },
  feed_beneficio: {
    bg: '#00A8B5',
    textColor: '#FFFFFF', eyebrowColor: 'rgba(255,255,255,.85)', accentColor: '#1A3461',
    stickerBg: '#1A3461', stickerColor: '#FFFFFF', stickerLabel: 'Beneficio',
    decorBubbles: true,
  },
  feed_testimonio: {
    bg: '#F1F5F9',
    textColor: '#0F172A', eyebrowColor: '#64748B', accentColor: '#00A8B5',
    stickerBg: '#FFFFFF', stickerColor: '#1A3461', stickerLabel: 'Testimonio',
    isQuote: true,
  },
  feed_novedad: {
    bg: 'linear-gradient(135deg, #1A3461 0%, #1A3461 100%)',
    textColor: '#FFFFFF', eyebrowColor: '#5BBDD8', accentColor: '#00A8B5',
    stickerBg: '#FFFFFF', stickerColor: '#1A3461', stickerLabel: 'Novedad',
  },
  story_novedad: {
    bg: 'linear-gradient(135deg, #070B14 0%, #1A3461 100%)',
    textColor: '#FFFFFF', eyebrowColor: '#00D4E0', accentColor: '#00D4E0',
    stickerBg: 'rgba(255,255,255,.18)', stickerColor: '#FFFFFF', stickerLabel: 'Esta semana',
    footerText: 'Desliza ↑',
  },
  story_pregunta: {
    bg: '#00A8B5',
    textColor: '#FFFFFF', eyebrowColor: 'rgba(255,255,255,.85)', accentColor: '#FFFFFF',
    stickerBg: '#FFFFFF', stickerColor: '#1A3461', stickerLabel: 'Tip rápido',
    footerText: 'Respondé el sticker',
  },
  story_behind: {
    bg: '#F1F5F9',
    textColor: '#0F172A', eyebrowColor: '#1A3461', accentColor: '#00A8B5',
    stickerBg: '#1A3461', stickerColor: '#FFFFFF', stickerLabel: 'Ruta del día',
  },
  story_promo: {
    bg: 'linear-gradient(160deg, #00D4E0 0%, #3B82F6 100%)',
    textColor: '#FFFFFF', eyebrowColor: 'rgba(255,255,255,.95)', accentColor: '#FFFFFF',
    stickerBg: '#FFFFFF', stickerColor: '#1A3461', stickerLabel: 'Promo del mes',
  },
  cierre_cta: {
    bg: 'linear-gradient(135deg, #1A3461 0%, #1A3461 100%)',
    textColor: '#FFFFFF', eyebrowColor: '#00D4E0', accentColor: '#00D4E0',
    isCTA: true,
  },
}
function getTemplateSpec(template) {
  return TEMPLATE_SPECS[template] || TEMPLATE_SPECS.feed_novedad
}
function stripAccent(s) {
  return (s || '').toString().replace(/\*\*([^*]+)\*\*/g, '$1')
}
function renderAccent(s, accentColor) {
  if (!s) return null
  return s.toString().split(/(\*\*[^*]+\*\*)/g).map((p, i) => {
    if (p.startsWith('**') && p.endsWith('**'))
      return <span key={i} style={{ color: accentColor }}>{p.slice(2, -2)}</span>
    return <span key={i}>{p}</span>
  })
}

// ── IGSlideRender v2 — render fiel del slide según template oficial v3.2 ──
function IGSlideRender({ slide, esStory }) {
  // Templates "cards Limpack" (brand-book, Fase 1 · card_*) → renderer nuevo.
  // Los feed_*/story_*/cierre_cta de abajo quedan LEGACY (compatibilidad).
  if (isCardLimpack(slide?.template)) {
    return <CardLimpackFrame slide={slide} esStory={esStory} />
  }

  const spec = getTemplateSpec(slide?.template)
  const aspectClass = esStory ? 'igsv2-story' : 'igsv2-feed'
  const tpl = slide?.template || 'feed_novedad'

  // Cierre CTA
  if (spec.isCTA) {
    return (
      <div className={`igsv2 ${aspectClass} igsv2-cta`} style={{ background: spec.bg, color: spec.textColor }}>
        <div className="igsv2-cta-logo" style={{ color: spec.textColor }}>
          Lim<em style={{ color: spec.accentColor }}>pack</em>
        </div>
        <div className="igsv2-cta-titulo">
          {renderAccent(slide?.titulo || '¿Arrancamos?', spec.accentColor)}
        </div>
        <div className="igsv2-cta-cta" style={{ color: spec.accentColor }}>→ limpack.uy</div>
        <div className="igsv2-cta-handles">@limpackuy · +598 98 610 001</div>
      </div>
    )
  }

  // Testimonio
  if (spec.isQuote) {
    return (
      <div className={`igsv2 ${aspectClass} igsv2-quote`} style={{ background: spec.bg, color: spec.textColor }}>
        {spec.stickerLabel && (
          <div className="igsv2-sticker" style={{ background: spec.stickerBg, color: spec.stickerColor }}>
            {slide?.sticker || spec.stickerLabel}
          </div>
        )}
        <div className="igsv2-quote-content">
          <div className="igsv2-comilla" style={{ color: spec.accentColor }}>"</div>
          <div className="igsv2-quote-text" style={{ color: spec.textColor }}>
            {stripAccent(slide?.quote || slide?.titulo || 'Me llega el día que pido y ya está.')}
          </div>
          <div className="igsv2-quote-atrib">{slide?.atribucion || '— Florencia · Pocitos'}</div>
        </div>
        <div className="igsv2-footer">
          <span className="igsv2-footer-link">limpack.uy</span>
          <span className="igsv2-footer-logo">Lim<em style={{ color: spec.accentColor }}>pack</em></span>
        </div>
      </div>
    )
  }

  // Beneficio
  if (tpl === 'feed_beneficio') {
    return (
      <div className={`igsv2 ${aspectClass} igsv2-benef`} style={{ background: spec.bg, color: spec.textColor }}>
        {spec.decorBubbles && (
          <>
            <div className="igsv2-bubble" style={{ width: 260, height: 260, top: -80, right: -70 }} />
            <div className="igsv2-bubble" style={{ width: 160, height: 160, bottom: 60, left: -50, opacity: .14 }} />
          </>
        )}
        {spec.stickerLabel && (
          <div className="igsv2-sticker" style={{ background: spec.stickerBg, color: spec.stickerColor, position: 'relative', zIndex: 2 }}>
            {slide?.sticker || spec.stickerLabel}
          </div>
        )}
        <div className="igsv2-benef-content" style={{ position: 'relative', zIndex: 2 }}>
          {slide?.dato_sub && (
            <div className="igsv2-eyebrow" style={{ color: spec.eyebrowColor }}>{stripAccent(slide.dato_sub)}</div>
          )}
          <div className="igsv2-numero-hero" style={{ color: spec.textColor }}>
            {slide?.dato_principal || stripAccent(slide?.titulo)?.slice(0, 6) || '12%'}
          </div>
          {slide?.dato_cap && <div className="igsv2-dato-cap">{stripAccent(slide.dato_cap)}</div>}
        </div>
        <div className="igsv2-footer" style={{ position: 'relative', zIndex: 2 }}>
          <span className="igsv2-footer-link" style={{ color: 'rgba(255,255,255,.85)' }}>limpack.uy</span>
          <span className="igsv2-footer-logo" style={{ color: '#fff' }}>
            Lim<em style={{ color: '#1A3461' }}>pack</em>
          </span>
        </div>
      </div>
    )
  }

  // Default (promo / novedad / story_*)
  const titulo = slide?.titulo || ''
  const hasDato = !!slide?.dato_principal && !titulo?.includes(slide.dato_principal)

  return (
    <div className={`igsv2 ${aspectClass}`} style={{ background: spec.bg, color: spec.textColor }}>
      {spec.stickerLabel && (
        <div className="igsv2-sticker" style={{ background: spec.stickerBg, color: spec.stickerColor }}>
          {slide?.sticker || spec.stickerLabel}
        </div>
      )}
      <div className="igsv2-body-area">
        {slide?.caption_top && (
          <div className="igsv2-eyebrow" style={{ color: spec.eyebrowColor }}>{stripAccent(slide.caption_top)}</div>
        )}
        <div className="igsv2-titulo" style={{ color: spec.textColor }}>
          {renderAccent(titulo || 'Algo simple por arrancar.', spec.accentColor)}
        </div>
        {hasDato && (
          <div className="igsv2-dato-grande" style={{ color: spec.accentColor }}>{slide.dato_principal}</div>
        )}
        {slide?.body && (
          <div className="igsv2-body" style={{ color: spec.textColor, opacity: .85 }}>{stripAccent(slide.body)}</div>
        )}
      </div>
      {(slide?.footer || spec.footerText) && (
        <div className="igsv2-foot-meta" style={{ color: spec.eyebrowColor }}>
          {slide?.footer ? stripAccent(slide.footer) : spec.footerText}
        </div>
      )}
      <div className="igsv2-footer">
        <span className="igsv2-footer-link" style={{ color: spec.eyebrowColor }}>limpack.uy</span>
        <span className="igsv2-footer-logo" style={{ color: spec.textColor }}>
          Lim<em style={{ color: spec.accentColor }}>pack</em>
        </span>
      </div>
    </div>
  )
}

// ── CSS ───────────────────────────────────────────────────────
function Estilos() {
  return (
    <style>{`
      /* — Wrap + Split layout (desktop-only Hub) — */
      .mh2-wrap {
        max-width: 1240px; margin: 0 auto; padding: 0 4px 60px;
      }
      .mh2-wrap-split {
        max-width: 1440px;
        display: grid;
        grid-template-columns: 1fr 400px;
        gap: 16px;
        align-items: start;
      }
      .mh2-main { min-width: 0; }
      .mh2-preview {
        position: sticky; top: 12px;
        max-height: calc(100vh - 24px);
        overflow-y: auto;
        background: #fff;
        border: 1px solid #E6E2D8;
        border-radius: 12px;
        padding: 14px;
        display: flex; flex-direction: column; gap: 12px;
      }
      .mh2-preview-head {
        display: flex; justify-content: space-between; align-items: flex-start;
        padding-bottom: 10px; border-bottom: 1px solid #F1F0EA;
      }
      .mh2-preview-eyebrow {
        font-size: 10px; letter-spacing: .8px; color: #6B7280;
      }
      .mh2-preview-titulo {
        font-size: 14px; font-weight: 600; color: #1A3461; margin-top: 2px;
      }
      .mh2-preview-cerrar {
        background: none; border: 0; font-size: 22px; color: #6B7280;
        cursor: pointer; line-height: 1; padding: 0 4px;
      }
      .mh2-preview-cerrar:hover { color: #1A3461; }
      .mh2-preview-tags {
        display: flex; flex-wrap: wrap; gap: 4px;
        padding: 8px; background: #F8F6F1; border-radius: 6px;
      }
      .mh2-preview-tags span {
        font-size: 10.5px; color: #00666B; font-weight: 500;
      }
      .mh2-preview-acts { display: flex; gap: 6px; padding-top: 4px; }
      .mh2-cb-rej-full {
        flex: 1; padding: 8px; border-radius: 5px; font-size: 12px;
        font-weight: 500; cursor: pointer; background: #fff; color: #DC2626;
        border: 1px solid #FECACA; font-family: inherit;
      }
      .mh2-cb-rej-full:hover { background: #FEF2F2; }

      /* — Chassis celular (compacto para panel 400px) — */
      .mh2-phone { display: flex; justify-content: center; }
      .mh2-phone-bezel {
        background: #0F172A; border-radius: 32px; padding: 6px;
        position: relative; width: 280px;
        box-shadow: 0 8px 24px rgba(15, 31, 61, .18);
      }
      .mh2-phone-notch {
        position: absolute; top: 10px; left: 50%; transform: translateX(-50%);
        width: 90px; height: 18px; background: #0F172A;
        border-radius: 0 0 12px 12px; z-index: 5;
      }
      .mh2-phone-notch::after {
        content: ''; position: absolute; top: 5px; left: 50%;
        transform: translateX(-50%); width: 40px; height: 5px;
        background: #000; border-radius: 3px;
      }
      .mh2-phone-screen {
        background: #fff; border-radius: 26px; overflow: hidden;
        display: flex; flex-direction: column;
      }
      .mh2-phone-sb {
        display: flex; justify-content: space-between; align-items: center;
        padding: 12px 18px 4px; font-size: 11px; font-weight: 600; color: #0F172A;
      }
      .mh2-phone-sb-icons { font-size: 10px; }
      .mh2-phone-igh {
        display: flex; align-items: center; gap: 8px;
        padding: 6px 12px; border-bottom: 0.5px solid #F1F5F9;
      }
      .mh2-phone-avatar {
        width: 26px; height: 26px; border-radius: 50%;
        background: linear-gradient(135deg, #1A3461, #00A8B5);
        color: #fff; display: flex; align-items: center; justify-content: center;
        font-weight: 700; font-size: 12px;
      }
      .mh2-phone-user { flex: 1; display: flex; flex-direction: column; line-height: 1.2; }
      .mh2-phone-user strong { font-size: 11.5px; color: #0F172A; }
      .mh2-phone-user span { font-size: 9.5px; color: #64748B; }
      .mh2-phone-dots { color: #0F172A; font-size: 14px; }
      .mh2-phone-img {
        position: relative; width: 100%; aspect-ratio: 4 / 5; background: #0F172A;
        overflow: hidden;
      }
      .mh2-phone-img-story { aspect-ratio: 9 / 16; }
      .mh2-phone-img img { width: 100%; height: 100%; object-fit: cover; display: block; }
      .mh2-phone-empty {
        height: 100%; display: flex; align-items: center; justify-content: center;
        color: #94A3B8; font-size: 11px;
      }
      .mh2-phone-counter {
        position: absolute; top: 8px; right: 8px;
        background: rgba(15, 23, 42, .65); color: #fff; font-size: 10px;
        font-weight: 600; padding: 2px 7px; border-radius: 10px;
      }
      .mh2-phone-nav {
        display: flex; gap: 4px; justify-content: center; padding: 6px 0;
        background: #fff;
      }
      .mh2-phone-dot {
        width: 6px; height: 6px; border-radius: 50%;
        background: #CBD5E1; border: 0; cursor: pointer; padding: 0;
      }
      .mh2-phone-dot.active { background: #00A8B5; }
      .mh2-phone-acts {
        display: flex; gap: 12px; padding: 6px 12px; font-size: 18px; color: #0F172A;
      }
      .mh2-phone-cap {
        padding: 0 12px 12px; font-size: 11.5px; line-height: 1.4; color: #0F172A;
      }
      .mh2-phone-cap strong { font-weight: 700; }

      /* — IGSlideRender v2 (escalado al panel 280px) — */
      .igsv2 {
        width: 100%; height: 100%;
        padding: 22px 18px;
        display: flex; flex-direction: column;
        position: relative; overflow: hidden;
        font-family: -apple-system, BlinkMacSystemFont, "Inter", "SF Pro Display", system-ui, sans-serif;
      }
      .igsv2-sticker {
        align-self: flex-start;
        font-size: 9px; font-weight: 800; letter-spacing: .04em;
        padding: 3px 9px; border-radius: 999px;
        margin-bottom: 10px;
      }
      .igsv2-body-area {
        flex: 1; display: flex; flex-direction: column; justify-content: center; gap: 8px;
      }
      .igsv2-eyebrow {
        font-size: 9px; font-weight: 800;
        text-transform: uppercase; letter-spacing: .14em;
        margin-bottom: 2px;
      }
      .igsv2-titulo {
        font-size: 20px; font-weight: 900; line-height: 1.05; letter-spacing: -.02em;
      }
      .igsv2-dato-grande {
        font-size: 40px; font-weight: 900; line-height: .95;
        letter-spacing: -.04em; margin-top: 6px;
      }
      .igsv2-body { font-size: 11px; line-height: 1.5; font-weight: 400; }
      .igsv2-foot-meta {
        font-size: 9px; font-weight: 700;
        text-transform: uppercase; letter-spacing: .12em;
        margin-top: 12px; opacity: .75;
      }
      .igsv2-footer {
        margin-top: auto; padding-top: 12px;
        display: flex; justify-content: space-between; align-items: center;
        font-size: 9.5px;
        border-top: 1px solid currentColor;
        border-top-color: rgba(255,255,255,.12);
      }
      .igsv2-footer-link { font-weight: 600; opacity: .75; }
      .igsv2-footer-logo { font-weight: 900; font-size: 11px; letter-spacing: -.02em; }
      .igsv2-footer-logo em { font-style: normal; }
      .igsv2-story { padding: 42px 22px 32px; }
      .igsv2-story .igsv2-titulo { font-size: 24px; }
      .igsv2-story .igsv2-dato-grande { font-size: 52px; }
      .igsv2-story .igsv2-body-area { justify-content: center; }
      /* Cierre CTA */
      .igsv2-cta { align-items: center; justify-content: center; text-align: center; gap: 12px; }
      .igsv2-cta-logo { font-size: 28px; font-weight: 900; letter-spacing: -.03em; }
      .igsv2-cta-logo em { font-style: normal; }
      .igsv2-cta-titulo { font-size: 16px; font-weight: 800; line-height: 1.15; letter-spacing: -.01em; }
      .igsv2-cta-cta { font-size: 13px; font-weight: 800; margin-top: 6px; }
      .igsv2-cta-handles { font-size: 9px; opacity: .65; margin-top: 10px; text-transform: uppercase; letter-spacing: .1em; }
      /* Testimonio */
      .igsv2-quote { justify-content: space-between; }
      .igsv2-quote-content { flex: 1; display: flex; flex-direction: column; justify-content: center; }
      .igsv2-comilla { font-family: Georgia, serif; font-size: 72px; line-height: .8; margin-bottom: -6px; }
      .igsv2-quote-text { font-size: 15px; font-weight: 700; line-height: 1.3; letter-spacing: -.01em; }
      .igsv2-quote-atrib { margin-top: 12px; font-size: 9px; font-weight: 700; text-transform: uppercase; letter-spacing: .12em; opacity: .65; }
      /* Beneficio */
      .igsv2-benef { padding: 22px 18px; position: relative; overflow: hidden; }
      .igsv2-benef .igsv2-body-area { align-items: center; text-align: center; }
      .igsv2-bubble { position: absolute; border-radius: 50%; background: rgba(255,255,255,.18); z-index: 1; }
      .igsv2-numero-hero { font-size: 64px; font-weight: 900; line-height: .9; letter-spacing: -.06em; }
      .igsv2-dato-cap { font-size: 11px; font-weight: 600; opacity: .85; margin-top: 4px; }
      .igsv2-story.igsv2-benef .igsv2-numero-hero { font-size: 84px; }

      /* — Card activo highlight (cuando está abierto en preview) — */
      .mh2-cola-activo {
        border-color: #00A8B5 !important;
        box-shadow: 0 0 0 2px rgba(0, 168, 181, .25);
      }

      /* — Tokens — */
      .mh2-head, .mh2-card, .mh2-kpi, .mh2-status, .mh2-cola-card, .mh2-dia {
        background: #fff; border: 1px solid #E6E2D8; border-radius: 10px;
      }

      /* — Header — */
      .mh2-head {
        display: flex; align-items: center; justify-content: space-between;
        padding: 12px 16px; margin-bottom: 10px; gap: 12px; flex-wrap: wrap;
      }
      .mh2-head-eyebrow { font-size: 10.5px; letter-spacing: .8px; color: #6B7280; }
      .mh2-head-title { margin: 2px 0 0; font-size: 18px; font-weight: 600; color: #1A3461; }
      .mh2-head-actions { display: flex; gap: 6px; flex-wrap: wrap; }
      .mh2-btn {
        padding: 8px 14px; border-radius: 8px; font-size: 13px; font-weight: 500;
        cursor: pointer; text-decoration: none; border: 1px solid transparent;
        font-family: inherit; transition: opacity .15s;
      }
      .mh2-btn-primary { background: #00A8B5; color: #fff; }
      .mh2-btn-primary:hover { opacity: .9; }
      .mh2-btn-primary:disabled { background: #94A3B8; cursor: wait; }
      .mh2-btn-ghost { background: #fff; color: #1A3461; border-color: #E6E2D8; }
      .mh2-btn-ghost:hover { background: #F8F6F1; }

      /* — Badge de piezas por día (reemplaza D+X en timeline) — */
      .mh2-dia-badge {
        background: #00A8B5; color: #fff; font-size: 10px; font-weight: 700;
        padding: 2px 7px; border-radius: 999px; letter-spacing: 0.02em;
      }

      /* — 🎯 Meta Ads tile (link al panel dedicado) — */
      .mh2-mads-tile {
        display: flex; justify-content: space-between; align-items: center;
        background: linear-gradient(135deg, #1a3461 0%, #1a5cb8 100%);
        border-radius: 12px; padding: 16px 20px; margin-bottom: 16px;
        text-decoration: none; color: white;
        transition: transform .12s, box-shadow .12s;
        box-shadow: 0 4px 14px rgba(26,52,97,.15);
      }
      .mh2-mads-tile:hover { transform: translateY(-1px); box-shadow: 0 6px 20px rgba(0,168,181,.25); }
      .mh2-mads-tile-left { display: flex; align-items: center; gap: 14px; }
      .mh2-mads-tile-icon {
        width: 48px; height: 48px; border-radius: 12px;
        background: rgba(255,255,255,0.15);
        display: flex; align-items: center; justify-content: center;
      }
      .mh2-mads-tile-title { font-size: 16px; font-weight: 800; letter-spacing: -0.01em; }
      .mh2-mads-tile-sub { font-size: 12px; opacity: 0.85; margin-top: 2px; }
      .mh2-mads-tile-caret { font-size: 22px; font-weight: 700; opacity: 0.7; }

      /* — 👥 Equipo Marketing — */
      .mh2-equipo {
        background: linear-gradient(135deg, rgba(26,52,97,0.03), rgba(0,168,181,0.05));
        border: 1px solid rgba(26,52,97,0.1);
        border-radius: 12px;
        padding: 16px 20px;
        margin-bottom: 16px;
      }
      .mh2-equipo-head {
        display: flex; justify-content: space-between; align-items: baseline;
        margin-bottom: 12px;
      }
      .mh2-equipo-head h2 {
        font-size: 15px; font-weight: 800; color: #1A3461; margin: 0;
      }
      .mh2-equipo-sub { font-size: 11px; color: #64748B; }
      .mh2-equipo-grid {
        display: grid;
        grid-template-columns: repeat(auto-fit, minmax(180px, 1fr));
        gap: 10px;
      }
      .mh2-agente {
        background: #fff; border: 1px solid #E5E7EB; border-radius: 10px;
        padding: 12px 14px; transition: border-color .15s;
      }
      .mh2-agente:hover { border-color: #00A8B5; }
      .mh2-agente-err { border-left: 3px solid #EF4444; }
      .mh2-agente-head {
        display: flex; align-items: center; gap: 10px; margin-bottom: 10px;
      }
      .mh2-agente-emoji { font-size: 22px; line-height: 1; }
      .mh2-agente-nombre { font-size: 13px; font-weight: 700; color: #1A3461; line-height: 1.1; }
      .mh2-agente-modelo { font-size: 10.5px; color: #64748B; margin-top: 2px; }
      .mh2-agente-stats {
        display: grid; grid-template-columns: repeat(3, 1fr); gap: 6px;
        padding-top: 8px; border-top: 1px solid #F1F5F9;
      }
      .mh2-agente-stat { display: flex; flex-direction: column; align-items: center; }
      .mh2-agente-stat-num { font-size: 14px; font-weight: 800; color: #1A3461; letter-spacing: -0.02em; }
      .mh2-agente-stat-lbl { font-size: 9.5px; color: #94A3B8; text-transform: uppercase; letter-spacing: 0.05em; margin-top: 2px; }
      .mh2-agente-err-badge {
        margin-top: 8px; padding: 4px 8px;
        background: #FEE2E2; color: #991B1B;
        font-size: 10.5px; font-weight: 700; border-radius: 4px;
      }

      /* — Modales inline (Anclar / Nueva pieza) — */
      .mh2-modal-back {
        position: fixed; inset: 0; background: rgba(15, 23, 42, 0.55); z-index: 100;
        display: flex; align-items: center; justify-content: center; padding: 20px;
        animation: mh2-fade .12s ease-out;
      }
      @keyframes mh2-fade { from { opacity: 0 } to { opacity: 1 } }
      .mh2-modal {
        background: #fff; border-radius: 12px; width: 100%; max-width: 520px;
        max-height: 90vh; overflow-y: auto; box-shadow: 0 20px 60px rgba(0,0,0,0.2);
      }
      .mh2-modal-head {
        display: flex; align-items: center; justify-content: space-between;
        padding: 16px 20px; border-bottom: 1px solid #F1F5F9;
      }
      .mh2-modal-head h3 { margin: 0; font-size: 15px; font-weight: 700; color: #1A3461; }
      .mh2-modal-x {
        background: none; border: none; font-size: 22px; color: #64748B;
        cursor: pointer; line-height: 1; padding: 0 4px;
      }
      .mh2-modal-x:hover { color: #1A3461; }
      .mh2-modal-body { padding: 18px 20px; }
      .mh2-modal-row { margin-bottom: 14px; }
      .mh2-modal-row label {
        display: block; font-size: 11.5px; font-weight: 700; color: #1A3461;
        margin-bottom: 5px; text-transform: uppercase; letter-spacing: 0.03em;
      }
      .mh2-modal-row input, .mh2-modal-row select, .mh2-modal-row textarea {
        width: 100%; padding: 9px 12px; border: 1px solid #CBD5E1; border-radius: 6px;
        font-size: 13px; font-family: inherit; box-sizing: border-box;
      }
      .mh2-modal-row textarea { resize: vertical; min-height: 80px; line-height: 1.4; }
      .mh2-modal-row input:focus, .mh2-modal-row select:focus, .mh2-modal-row textarea:focus {
        outline: none; border-color: #00A8B5; box-shadow: 0 0 0 3px rgba(0, 168, 181, 0.15);
      }
      .mh2-modal-row-2col { display: grid; grid-template-columns: 1fr 1fr; gap: 12px; margin-bottom: 14px; }
      .mh2-modal-row-2col label {
        display: block; font-size: 11.5px; font-weight: 700; color: #1A3461;
        margin-bottom: 5px; text-transform: uppercase; letter-spacing: 0.03em;
      }
      .mh2-modal-row-2col input, .mh2-modal-row-2col select {
        width: 100%; padding: 9px 12px; border: 1px solid #CBD5E1; border-radius: 6px;
        font-size: 13px; font-family: inherit; box-sizing: border-box;
      }
      .mh2-radio-group { display: flex; gap: 8px; flex-wrap: wrap; }
      .mh2-radio {
        display: inline-flex; align-items: center; gap: 6px;
        padding: 8px 12px; border: 1px solid #CBD5E1; border-radius: 6px;
        font-size: 12.5px; cursor: pointer; background: #fff; user-select: none;
      }
      .mh2-radio.active { border-color: #00A8B5; background: #F0FAFB; color: #005961; font-weight: 600; }
      .mh2-radio input { position: absolute; opacity: 0; pointer-events: none; }
      .mh2-modal-err {
        background: #FEE2E2; border: 1px solid #FCA5A5; color: #991B1B;
        padding: 8px 12px; border-radius: 6px; font-size: 12px; margin: 8px 0;
      }
      .mh2-modal-foot {
        display: flex; justify-content: flex-end; gap: 10px;
        padding-top: 14px; border-top: 1px solid #F1F5F9; margin-top: 8px;
      }

      /* — Feedback — */
      .mh2-fb {
        position: relative; padding: 10px 36px 10px 14px; border-radius: 8px;
        margin-bottom: 10px; font-size: 13px;
      }
      .mh2-fb-ok { background: #ECFDF5; color: #065F46; border-left: 3px solid #10B981; }
      .mh2-fb-err { background: #FEF2F2; color: #991B1B; border-left: 3px solid #DC2626; }
      .mh2-fb-meta { font-size: 11px; color: #6B7280; margin-left: 8px; }
      .mh2-fb-resumen { margin-top: 4px; font-size: 12.5px; line-height: 1.5; opacity: .9; }
      .mh2-fb-x {
        position: absolute; top: 6px; right: 10px; background: none; border: 0;
        font-size: 18px; cursor: pointer; color: inherit; opacity: .6;
      }

      /* — KPIs — */
      .mh2-kpis {
        display: grid; grid-template-columns: repeat(4, 1fr); gap: 8px; margin-bottom: 10px;
      }
      @media (max-width: 720px) { .mh2-kpis { grid-template-columns: repeat(2, 1fr); } }
      .mh2-kpi { border-left: 3px solid; padding: 10px 12px; }
      .mh2-kpi-hl { box-shadow: 0 2px 8px rgba(0, 168, 181, .12); }
      .mh2-kpi-titulo { font-size: 10px; letter-spacing: .8px; color: #6B7280; }
      .mh2-kpi-valor { font-size: 22px; font-weight: 600; margin: 2px 0; line-height: 1; }
      .mh2-kpi-sub { font-size: 11px; color: #6B7280; }

      /* — Card genérica — */
      .mh2-card { padding: 12px; margin-bottom: 10px; }
      .mh2-card-head {
        display: flex; align-items: center; justify-content: space-between;
        margin-bottom: 8px; gap: 8px; flex-wrap: wrap;
      }
      .mh2-card-title {
        margin: 0; font-size: 13px; font-weight: 600; color: #1A3461;
        display: flex; align-items: center; gap: 6px;
      }
      .mh2-badge-count {
        background: #00A8B5; color: #fff; font-size: 11px; padding: 2px 8px;
        border-radius: 10px; font-weight: 600;
      }
      .mh2-legend { display: flex; gap: 10px; font-size: 11px; color: #6B7280; }
      .mh2-legend span { display: inline-flex; align-items: center; gap: 4px; }
      .mh2-legend i { width: 8px; height: 8px; border-radius: 2px; display: inline-block; }
      .mh2-link-mini { font-size: 11px; color: #00A8B5; text-decoration: none; font-weight: 500; }
      .mh2-link-mini:hover { text-decoration: underline; }

      /* — Timeline — */
      .mh2-timeline {
        display: grid; grid-template-columns: repeat(7, 1fr); gap: 6px;
      }
      @media (max-width: 900px) { .mh2-timeline { grid-template-columns: repeat(4, 1fr); } }
      @media (max-width: 580px) { .mh2-timeline { grid-template-columns: repeat(2, 1fr); } }
      .mh2-dia {
        background: #FAFAF7; border-top: 3px solid; padding: 8px;
        min-height: 100px; display: flex; flex-direction: column; gap: 4px;
      }
      .mh2-dia-dday { background: #FFFBEF; box-shadow: inset 0 0 0 2px #C5A059; }
      .mh2-dia-head {
        display: flex; justify-content: space-between; align-items: center;
        margin-bottom: 4px;
      }
      .mh2-dia-dow { font-size: 11px; color: #6B7280; font-weight: 500; }
      .mh2-dia-tag {
        font-size: 10px; font-weight: 600; color: #1A3461;
        background: #F1F0EA; padding: 1px 5px; border-radius: 4px;
      }
      .mh2-dia-tag-gold { background: #C5A059; color: #fff; }
      .mh2-anc {
        display: flex; align-items: center; gap: 4px;
        padding: 3px 6px; border-radius: 4px; font-size: 10px;
        border: 0; font-family: inherit; text-align: left;
      }
      .mh2-anc-click {
        cursor: pointer;
        transition: transform .1s, box-shadow .1s;
      }
      .mh2-anc-click:hover {
        transform: scale(1.02);
        box-shadow: 0 2px 6px rgba(0, 0, 0, .12);
      }
      .mh2-anc-activo {
        box-shadow: 0 0 0 2px #00A8B5;
      }
      .mh2-anc-hora { font-weight: 600; }
      .mh2-anc-fmt { flex: 1; white-space: nowrap; overflow: hidden; text-overflow: ellipsis; }
      .mh2-anc-ok, .mh2-anc-pend, .mh2-anc-pub { font-weight: 700; }

      /* — Tabs cola — */
      .mh2-tabs {
        display: flex; gap: 4px;
      }
      .mh2-tab {
        background: transparent; border: 0; padding: 6px 12px;
        font-size: 13px; font-weight: 500; color: #6B7280;
        cursor: pointer; border-radius: 6px;
        display: inline-flex; align-items: center; gap: 6px;
        font-family: inherit;
      }
      .mh2-tab:hover { background: #F8F6F1; color: #1A3461; }
      .mh2-tab.active {
        background: #1A3461; color: #fff;
      }
      .mh2-badge-green { background: #10B981; }

      /* — Lista para publicar card — */
      .mh2-lista-card { background: #ECFDF5; border-color: #10B981; }
      .mh2-lista-render {
        font-size: 11px; color: #6B7280; padding: 4px 6px;
        background: #fff; border-radius: 4px; font-style: italic;
      }
      .mh2-cb-publicar {
        background: #00A8B5; color: #fff; font-weight: 600;
      }
      .mh2-cb-publicar:disabled {
        background: #94A3B8; cursor: not-allowed;
      }

      /* — Badge PREVIEW en celular (cuando no hay Ideogram) — */
      .mh2-phone-overlay-mock {
        position: absolute; top: 8px; left: 8px;
        background: rgba(255, 255, 255, .85); color: #1A3461;
        font-size: 9px; font-weight: 700; letter-spacing: .8px;
        padding: 2px 6px; border-radius: 4px;
      }
      .mh2-dia-empty {
        background: #F1F0EA; border: 1px dashed #B5B0A0;
        padding: 6px; border-radius: 4px; font-size: 10px; color: #6B7280;
        text-align: center; text-decoration: none; transition: background .15s;
      }
      .mh2-dia-empty:hover { background: #E6E2D8; }

      /* — Cola — */
      .mh2-cola {
        display: grid; grid-template-columns: repeat(auto-fill, minmax(280px, 1fr)); gap: 8px;
      }
      .mh2-cola-card {
        background: #FAFAF7; padding: 10px 12px;
        display: flex; flex-direction: column; gap: 6px;
      }
      .mh2-cola-dday { background: #FFFBEF; border-color: #C5A059; }
      .mh2-cola-head { display: flex; justify-content: space-between; align-items: center; }
      .mh2-cola-fecha {
        background: #1A3461; color: #fff; font-size: 10px; padding: 2px 7px;
        border-radius: 4px; font-weight: 600;
      }
      .mh2-cola-fecha-gold { background: #C5A059; }
      .mh2-cola-fmt {
        background: #E0F4F6; color: #00666B; font-size: 10px; padding: 2px 7px;
        border-radius: 4px; font-weight: 500;
      }
      .mh2-cola-pilar { font-size: 10px; color: #6B7280; }
      .mh2-cola-titulo { font-size: 12.5px; font-weight: 600; color: #1A3461; }
      .mh2-cola-cap { font-size: 11.5px; color: #4B5563; line-height: 1.4; }
      .mh2-cola-acciones { display: flex; gap: 4px; margin-top: 4px; }
      .mh2-cb {
        flex: 1; padding: 6px; border-radius: 5px; font-size: 11.5px;
        font-weight: 500; cursor: pointer; border: 1px solid transparent;
        text-decoration: none; text-align: center; font-family: inherit;
        transition: opacity .15s;
      }
      .mh2-cb-ok { background: #10B981; color: #fff; }
      .mh2-cb-edit { background: #fff; color: #1A3461; border-color: #E6E2D8; }
      .mh2-cb-rej { flex: 0 0 36px; background: #fff; color: #DC2626; border-color: #E6E2D8; }
      .mh2-cb:hover { opacity: .85; }

      /* — Empty states — */
      .mh2-empty {
        background: #F8F6F1; border: 1px dashed #B5B0A0; border-radius: 8px;
        padding: 24px; text-align: center; color: #6B7280;
      }
      .mh2-empty strong { display: block; margin-bottom: 4px; color: #1A3461; }
      .mh2-empty p { margin: 0; font-size: 13px; }
      .mh2-empty-mini {
        background: #F8F6F1; border: 1px dashed #B5B0A0; border-radius: 6px;
        padding: 16px; text-align: center; color: #6B7280; font-size: 13px;
      }

      /* — Footer status — */
      .mh2-footer {
        display: grid; grid-template-columns: 1fr 1fr; gap: 8px;
      }
      @media (max-width: 600px) { .mh2-footer { grid-template-columns: 1fr; } }
      .mh2-status { padding: 10px 12px; }
      .mh2-status-label { font-size: 10px; letter-spacing: .8px; color: #6B7280; }
      .mh2-status-value {
        margin-top: 4px; display: flex; align-items: center; gap: 6px;
        font-size: 12.5px; font-weight: 600; color: #1A3461;
      }
      .mh2-status-meta { font-size: 11px; color: #6B7280; margin-top: 4px; }
      .mh2-dot { width: 8px; height: 8px; border-radius: 50%; display: inline-block; }
      .mh2-dot-ok { background: #10B981; }
      .mh2-dot-gold { background: #C5A059; }
    `}</style>
  )
}
